import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route as RouteIcon } from 'lucide-react';
import { TopBar } from './TopBar';
import { BottomToolbar } from '../map-panels/BottomToolbar';
// import { ModuleStoreDebug } from './components'; // Deshabilitado temporalmente
import { UrbanNetworkLoaderV4 } from './components';
import { useModuleStore } from '../module-store';
import type { ModuleId } from '../module-store/types';
import { LeftPanel, RightPanel, usePanelCoordination, RightPanelProvider, useRightPanel } from './panels';
import { EditLockProvider, useEditLock } from './EditLockContext';
import { NetworksAndGridsPanel } from './panels/LeftPanel/NetworksAndGridsPanel';
import { AnalyzePanel } from './panels/LeftPanel/AnalyzePanel';
import { PopulationAnalysisPanel } from './panels/LeftPanel/PopulationAnalysisPanel';
import { MapToolsProvider, useMapTools } from '../map-tools';
import { TreeDetailsPanel } from '../map-tools/TreeDetailsPanel';
import { FountainDetailsPanel } from '../map-tools/FountainDetailsPanel';
import { ContainerDetailsPanel as LocalContainerDetailsPanel } from '../map-tools/ContainerDetailsPanel';
import {
  GridCellsPanel,
  CellDetailsPanel,
  CellMapPopover,
  RoadsListPanel,
  NodeDetailsPanel,
  EdgeDetailsPanel,
  type EdgeDetailTab,
  NetworkAnalysisOverviewPanel,
} from './panels/RightPanel';
import {
  CreateRoutePanel,
  ContainerDetailsPanel as WasteContainerDetailsPanel,
  DepotDetailsPanel as WasteDepotDetailsPanel,
  HotspotDetailsPanel as WasteHotspotDetailsPanel,
} from '../modules/waste/components/panels';
import { useRoutes, useRoutesMapIntegration } from '../modules/waste/hooks';
import { ElementPreview, undoRedoService, UndoRedoKeyboardHandler, UndoRedoControls } from '../map-tools';
import { IndicatorLegend, ZonalStatsPanel } from '../analysis';
import { getEntityConfig, useNewEntitiesTracking, registerAllEntities } from '../entity-registry';
import type { IndicatorType, ClassificationResult, SelectedArea, IndicatorResult } from '../analysis';
import type { UndoCommand } from '../map-tools';
import { GridEditPanel } from '../map-tools/h3-grid';
import { GridsProvider, useGrids, gridApi, UrbanNetworkProvider, useUrbanNetwork, BaseNetworkProvider, useBaseNetwork, NetworkStyleProvider, NetworkStylePanel, NetworkEditProvider, useNetworkEdit, useNetworkEditInteractivity, NetworkEditConfirmation, NetworkEditModeIndicator, useMidPointDetection, MidPointHandle, useSnapDetection, SyncStatusIndicator, NetworkUndoRedoHandler, EdgeContextMenu, NodeContextMenu, SyntaxAnalysisProvider, useSyntaxAnalysis, useSyntaxVisualization, SyntaxLegend, useNetworkHierarchy, useNetworkHierarchyLayer, useNetworkHierarchyVisibilityStore, useNetworkAnalysisUiStore, useEdgeGeometryEdit, useEdgeGeometryEditOnDoubleClick, canonicalNetworkStore, PopulationSegmentProvider, usePopulationSegment, usePopulationVisualization, usePopulationSegmentInteractivity } from '../spatial';
import type { SpatialGrid } from '../spatial';
import { ScenarioBanner } from './ScenarioBanner';
import { ScenariosPanel } from './panels/LeftPanel/ScenariosPanel';
import type { Scenario } from '../scenarios';
import { useOperationalModeStore } from '../operational-modes';
import { mapEngine, isMapReadyForSources } from '../map-engine/MapEngineService';
import { MapViewer, mapStyleService } from '../map-engine';
import { useProject, projectsApi } from '../projects';
import type { Project } from '../projects';
import { markOpeningPerfEvent, startOpeningPerfSession } from '../utils';
import { useEnvironmentalLayers } from '../modules/environmental/hooks';
import { useWasteLayers } from '../modules/waste/hooks';
import { useBuildingLayers } from '../modules/urban/hooks';
import { useBuildingsLayerStore } from '../buildings/buildings-layer-store';
import { BuildingDetailsSidebar } from '../buildings/BuildingDetailsSidebar';
import { useCensusRadioLayer } from '../census-2022/useCensusRadioLayer';
import { useCensusRadioLayerStore } from '../census-2022/census-radio-layer-store';
import { CensusRadioDetailsPanel } from '../census-2022/CensusRadioDetailsPanel';
import { wasteApi } from '../modules/waste/services';
import type { DayOfWeek, WasteContainer, WasteDepot, WasteHotspot } from '../modules/waste/types';
import { BuildingImportWizardModal } from '../spatial/import-wizard/BuildingImportWizardModal';
import { canonicalNetworkApi } from '../spatial/services/canonical-network-api.service';
import { useCanonicalEdgeSemanticHover } from '../spatial/hooks/useCanonicalEdgeSemanticHover';
import {
  getEdgeNamingStatusLabel,
  getEdgeNamingStatusTone,
  getEdgeSemanticDisplayName,
} from '../spatial/utils/edge-semantics-ui';
// Side-effect imports: auto-registran cada módulo en el moduleRegistry
import '../modules/environmental';
import '../modules/mobility';
import { TransitStopDetailsPanel } from '../modules/mobility/components';
import { mobilityApi } from '../modules/mobility/services';
import type { TransitStop } from '../modules/mobility/types/stop.types';
import '../modules/roadways';
import '../modules/waste';
import '../modules/urban-network';
import '../modules/infrastructure';
import '../modules/urban';
import '../modules/social';
import '../modules/services';
import '../modules/safety';
import '../modules/urban-inventory';
import { UrbanEntityDetailsPanel, useUrbanEntities, useUrbanEntitiesLayer, useUrbanEntityInteractivity, useUrbanInventoryStore } from '../modules/urban-inventory';
// Module Registry - Permite renderizar módulos dinámicamente
import { useModule } from '../module-registry';
import './map-layout.css';
import './scenario-banner.css';
import '../map-panels/map-panels.css';

/**
 * Props para MapLayout
 */
interface MapLayoutProps {
  /**
   * Mostrar paneles laterales y toolbar
   * @default true
   * @remarks
   * - true: Muestra LayersPanel, InsightsPanel y BottomToolbar (para /dashboard)
   * - false: Solo muestra TopBar + contenido (para /admin/dashboard)
   */
  showPanels?: boolean;
  /** Modos visibles en el segmented control. Si no se pasa, muestra todos. */
  visibleModes?: import('../operational-modes').OperationalMode[];
}

interface NetworkEdgeHoverDetail {
  elementId: string | null;
  clientX: number;
  clientY: number;
}

/**
 * NetworkLoadingScreen — Pantalla de carga full-screen de Red Urbana.
 *
 * @remarks
 * Visible desde t=0 hasta que el SPIKE preview esté activo (fade-out)
 * y completamente desmontada cuando la red termina su primera carga.
 * Gradiente oscuro azulado/cyan — sin negros puros.
 */
interface NetworkLoadingScreenProps {
  /** Cuando true inicia el fade-out y revela el mapa con SPIKE */
  isFadingOut: boolean;
}

function NetworkLoadingScreen({ isFadingOut }: NetworkLoadingScreenProps): JSX.Element {
  return (
    <div
      className={`nls${isFadingOut ? ' nls--fade-out' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Cargando red urbana"
    >
      <UrbanNetworkLoaderV4 size="xl" />
      <span className="nls__label">Cargando</span>
    </div>
  );
}

/**
 * MapInitializationGate - REMOVIDO
 *
 * @remarks
 * El gate fue removido porque empeoró la experiencia en runtime:
 * - Se quedaba colgado (listener del mapa nunca se registraba)
 * - Entraba demasiado temprano (antes de que el mapa cargara)
 * - Daba sensación de mayor espera, no menor
 *
 * Las correcciones que SÍ funcionaron y se mantienen:
 * - Trees no auto-loading (MapToolsContext.tsx)
 * - Loading indicator bien posicionado (map-layout.css)
 */

function getSelectableFeatureId(feature: unknown): string | null {
  if (!feature || typeof feature !== 'object') {
    return null;
  }

  const maybeFeature = feature as { id?: string | number };
  return maybeFeature.id !== undefined && maybeFeature.id !== null
    ? String(maybeFeature.id)
    : null;
}

// ─── Fase 2C: Project Bootstrap Cache ────────────────────────────────────────
// Cachea bounds + metadata del proyecto en localStorage para crear el mapa
// inmediatamente en el siguiente reload, sin esperar project:detail-fetch-end.

const BOOTSTRAP_CACHE_VERSION = 1;
const BOOTSTRAP_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días — bounds raramente cambian

interface ProjectBootstrapCache {
  version: number;
  projectId: string;
  bounds: {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
  };
  projectName: string;
  modules?: string[];
  cachedAt: number;
}

function _bootstrapCacheKey(projectId: string): string {
  return `wayline-project-bootstrap-${projectId}`;
}

function readProjectBootstrapCache(projectId: string): ProjectBootstrapCache | null {
  try {
    const raw = localStorage.getItem(_bootstrapCacheKey(projectId));
    if (!raw) return null;
    const data = JSON.parse(raw) as ProjectBootstrapCache;
    if (data.version !== BOOTSTRAP_CACHE_VERSION) return null;
    if (data.projectId !== projectId) return null;
    const { bounds } = data;
    if (!bounds ||
        typeof bounds.minLng !== 'number' ||
        typeof bounds.minLat !== 'number' ||
        typeof bounds.maxLng !== 'number' ||
        typeof bounds.maxLat !== 'number') return null;
    if (Date.now() - data.cachedAt > BOOTSTRAP_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeProjectBootstrapCache(project: { id: string; name: string; bounds?: { minLng: number; minLat: number; maxLng: number; maxLat: number } | null; activeModules?: string[] }): void {
  try {
    if (!project.bounds ||
        typeof project.bounds.minLng !== 'number') return;
    const cache: ProjectBootstrapCache = {
      version: BOOTSTRAP_CACHE_VERSION,
      projectId: project.id,
      bounds: project.bounds,
      projectName: project.name,
      modules: project.activeModules ?? undefined,
      cachedAt: Date.now(),
    };
    localStorage.setItem(_bootstrapCacheKey(project.id), JSON.stringify(cache));
  } catch {
    // localStorage lleno o no disponible — ignorar silenciosamente
  }
}

function invalidateProjectBootstrapCache(projectId: string): void {
  try {
    localStorage.removeItem(_bootstrapCacheKey(projectId));
  } catch {
    // ignorar
  }
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * MapLayout - Layout fullscreen con topbar horizontal y paneles
 *
 * @version 2.0
 * @module core/layouts
 *
 * @description
 * Layout diseñado específicamente para vistas fullscreen con mapa de fondo.
 * Incluye topbar horizontal, paneles laterales y toolbar inferior con glassmorphism.
 *
 * @remarks
 * **Características**:
 * - Fullscreen (100vw x 100vh)
 * - TopBar fixed en la parte superior (z-index: var(--z-navbar))
 * - LayersPanel lateral izquierdo (colapsable)
 * - InsightsPanel lateral derecho (colapsable)
 * - BottomToolbar fijo en la parte inferior
 * - Todos los paneles usan glassmorphism intenso
 * - Área de contenido que ocupa todo el viewport
 *
 * **Uso**:
 * - `/dashboard`: Con paneles (showPanels=true)
 * - `/admin/dashboard`: Sin paneles (showPanels=false)
 *
 * @example
 * ```tsx
 * import { MapLayout } from '@/core/layouts';
 * import { MapViewer } from '@/core/map-engine';
 *
 * // Dashboard de usuario con paneles
 * function DashboardPage() {
 *   return (
 *     <MapLayout showPanels={true}>
 *       <MapViewer height="100vh" width="100%" />
 *     </MapLayout>
 *   );
 * }
 *
 * // Dashboard de admin sin paneles
 * function AdminDashboardPage() {
 *   return (
 *     <MapLayout showPanels={false}>
 *       <ProjectsList />
 *     </MapLayout>
 *   );
 * }
 * ```
 */
/**
 * Componente interno que usa el hook useMapTools
 */
function MapLayoutContent({
  showPanels,
  visibleModes,
}: MapLayoutProps): JSX.Element {
  // Registrar entidades al iniciar (solo una vez)
  useEffect(() => {
    registerAllEntities();
  }, []);

  // Cargar capas reales de Environmental (árboles)
  useEnvironmentalLayers();

  // Cargar capas reales de Waste (contenedores, depósitos, rutas)
  useWasteLayers();

  // Cargar capas de Edificios (building_centroids con clustering)
  useBuildingLayers();

  // Estado de revisión de edificios (panel lateral derecho V1.4)
  const selectedBuildingForReview = useBuildingsLayerStore((s) => s.selectedBuildingForReview);
  const closeBuildingReview       = useBuildingsLayerStore((s) => s.closeBuildingReview);
  const triggerBuildingReload     = useBuildingsLayerStore((s) => s.triggerReload);

  // Cargar capa de radios censales 2022
  useCensusRadioLayer();
  const selectedCensusRadio = useCensusRadioLayerStore((s) => s.selectedRadio);
  const clearCensusRadio    = useCensusRadioLayerStore((s) => s.clearRadio);

  // Sistema de bloqueo de edición
  const { isEditingActive, hasUnsavedChanges } = useEditLock();

  // Hook genérico para tracking de entidades recién creadas
  const { track: trackNewEntity, isTracked: isEntityTracked, untrack: untrackEntity } = useNewEntitiesTracking();

  // Obtener módulo activo y funciones del module store
  const { activeModule, setAvailableModules } = useModuleStore();

  // Hook de coordinación de paneles (nuevo sistema)
  const {
    leftPanelVisible,
    rightPanelVisible,
    selectedFeature,
    toggleLeftPanel,
    closeRightPanel,
    selectFeature,
  } = usePanelCoordination();

  // Hook del RightPanel para verificar cambios sin guardar
  const { safeClose, canChangeContent, activePanel, setActivePanel } = useRightPanel();
  const [semanticHover, setSemanticHover] = useState<NetworkEdgeHoverDetail | null>(null);

  // Hook de herramientas del mapa
  const {
    activeTool, setActiveTool,
    trees, selectedTreeId, selectTree, addTree, updateTree, removeTree, saveTree,
    fountains, selectedFountainId, selectFountain, addFountain, updateFountain, removeFountain, saveFountain,
    containers, selectedContainerId, selectContainer, addContainer, updateContainer, removeContainer,
  } = useMapTools();

  // Obtener proyecto activo y proyectos disponibles
  const { projectId, hasProjects, isLoading: isLoadingProjects, availableProjects } = useProject();
  const navigate = useNavigate();

  // ─── Urban Inventory — Fase 2A (visualización, solo lectura) ──────────────
  const { entities: urbanEntities } = useUrbanEntities({
    projectId: projectId ?? '',
    autoLoad: !!projectId,
  });
  const urbanLayerVisible = useUrbanInventoryStore((s) => s.layerVisible);
  const selectedUrbanEntity = useUrbanInventoryStore((s) => s.selectedEntity);
  const selectUrbanEntity = useUrbanInventoryStore((s) => s.selectEntity);
  useUrbanEntitiesLayer({ entities: urbanEntities, visible: urbanLayerVisible });
  const handleUrbanEntityDeselected = useCallback(() => { closeRightPanel(); }, [closeRightPanel]);
  useUrbanEntityInteractivity({
    entities: urbanEntities,
    onEntitySelected: () => { /* store actualizado; RightPanel reacciona automáticamente */ },
    onEntityDeselected: handleUrbanEntityDeselected,
  });

  // ── Fase 2C: Bootstrap cache — bounds cacheados para crear mapa sin esperar API ──
  // useMemo ejecuta sincrónicamente durante el render; si hay hit, MapViewer se monta
  // de inmediato sin esperar project:detail-fetch-end.
  const cachedBootstrap = useMemo<ProjectBootstrapCache | null>(() => {
    if (!projectId) return null;
    return readProjectBootstrapCache(projectId);
  }, [projectId]);
  // Flag: true si el mapa fue montado con bounds del cache (no del API real aún)
  const mapCreatedFromCacheRef = useRef(false);
  const [selectedWasteContainer, setSelectedWasteContainer] = useState<WasteContainer | null>(null);
  const [selectedWasteDepot, setSelectedWasteDepot] = useState<WasteDepot | null>(null);
  const [selectedWasteHotspot, setSelectedWasteHotspot] = useState<WasteHotspot | null>(null);
  const [selectedTransitStop, setSelectedTransitStop] = useState<TransitStop | null>(null);

  // ── Loading Gate: REMOVIDO (empeoró la experiencia en runtime) ──
  // El gate se quedaba colgado porque el listener del mapa nunca se registraba correctamente.
  // Además, entraba demasiado temprano y daba sensación de mayor espera.
  // Correcciones efectivas que SÍ se mantienen:
  // - Trees no auto-loading (MapToolsContext)
  // - Loading indicator bien posicionado (map-layout.css)

  /**
   * Verificar si el usuario tiene proyectos disponibles
   * Si no tiene proyectos, redirigir a /no-projects
   */
  useEffect(() => {
    // Esperar a que termine de cargar los proyectos
    if (isLoadingProjects) return;

    // Si no hay proyectos disponibles, redirigir
    if (!hasProjects) {
      console.warn('[MapLayout] Usuario sin proyectos disponibles, redirigiendo a /no-projects');
      navigate('/no-projects', { replace: true });
    }
  }, [isLoadingProjects, hasProjects, navigate]);

  // ── Hook de red urbana (para interactividad y selección) ──
  // REFACTOR: hoveredEdgeId ahora viene de UrbanNetworkContext (único owner del hover)
  const { isNetworkInteractive, setSelectedEdgeId: setUrbanNetworkSelectedEdgeId, hoveredEdgeId, loadingNetwork, previewNetworkBooting, previewNetworkActive } = useUrbanNetwork();
  const [hasObservedInitialNetworkLoad, setHasObservedInitialNetworkLoad] = useState(false);
  const [initialNetworkLoadComplete, setInitialNetworkLoadComplete] = useState(false);
  const openingPerfSessionRef = useRef<string | null>(null);
  const toolsEnabledLoggedRef = useRef(false);

  // ── Fase 2D.2: tres niveles de readiness del mapa, todos independientes de red ──
  //
  // mapShellReady   — objeto MapLibre existe, MapEngine registrado (~map:create-end)
  //                   Desbloquea UI básica: paneles, chat, módulos, navegación.
  // mapStyleReady   — primer styledata:style parseado, canvas safe para capas
  //                   Fade-out del NetworkLoadingScreen + canvas revela.
  // mapVisualReady  — map:load event, tiles cargados (puede ser variable en tiempo)
  //                   Solo perf logging.
  const [mapShellReady, setMapShellReady] = useState(false);
  const [mapStyleReady, setMapStyleReady] = useState(false);
  const networkPreviewLoggedRef = useRef(false);
  const fullNetworkLoggedRef = useRef(false);

  const handleMapShellReady = useCallback(() => {
    setMapShellReady(true);
    markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:map-shell-ready');
    markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:basic-tools-enabled');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMapStyleReady = useCallback(() => {
    setMapStyleReady(true);
    markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:map-style-ready');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMapLoad = useCallback(() => {
    markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:map-visual-ready');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) {
      openingPerfSessionRef.current = null;
      toolsEnabledLoggedRef.current = false;
      return;
    }

    openingPerfSessionRef.current = startOpeningPerfSession(projectId);
    toolsEnabledLoggedRef.current = false;
  }, [projectId]);

  // ── Fase 2C: Log cache hit/miss después de crear la sesión de performance ──
  useEffect(() => {
    if (!projectId) return;
    const sessionId = openingPerfSessionRef.current;
    if (cachedBootstrap) {
      console.log('[project-bootstrap-cache] hit', {
        projectId,
        projectName: cachedBootstrap.projectName,
        cachedAt: new Date(cachedBootstrap.cachedAt).toISOString(),
        bounds: cachedBootstrap.bounds,
      });
      markOpeningPerfEvent(sessionId, 'project-bootstrap-cache:hit');
      markOpeningPerfEvent(sessionId, 'map:create-from-cached-bounds');
      mapCreatedFromCacheRef.current = true;
    } else {
      console.log('[project-bootstrap-cache] miss', { projectId });
      markOpeningPerfEvent(sessionId, 'project-bootstrap-cache:miss');
      mapCreatedFromCacheRef.current = false;
    }
  }, [projectId, cachedBootstrap]);

  useEffect(() => {
    setHasObservedInitialNetworkLoad(false);
    setInitialNetworkLoadComplete(false);
    // mapShellReady NOT reset here — starts false by default (useState), set by onMapShellReady.
    // Resetting here causes StrictMode race: parent effect runs AFTER child MapViewer already
    // called onMapShellReady → setMapShellReady(true), so this override leaves it stuck at false.
    setMapStyleReady(false);
    networkPreviewLoggedRef.current = false;
    fullNetworkLoggedRef.current = false;
    toolsEnabledLoggedRef.current = false;
  }, [projectId]);

  useEffect(() => {
    console.log(`[NetworkLoadPerf] MapLayout state — loadingNetwork:${loadingNetwork} hasObserved:${hasObservedInitialNetworkLoad} loadComplete:${initialNetworkLoadComplete}`);
    if (loadingNetwork) {
      setHasObservedInitialNetworkLoad(true);
      return;
    }

    if (hasObservedInitialNetworkLoad) {
      console.log('[NetworkLoadPerf] MapLayout → setInitialNetworkLoadComplete(true)');
      setInitialNetworkLoadComplete(true);
    }
  }, [hasObservedInitialNetworkLoad, loadingNetwork]);

  // ── Fase 2D: Log ui:network-preview-ready cuando SPIKE preview es visible ──
  useEffect(() => {
    if (previewNetworkActive && !networkPreviewLoggedRef.current) {
      markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:network-preview-ready');
      networkPreviewLoggedRef.current = true;
    }
  }, [previewNetworkActive]);

  // ── Fase 2D: Log ui:full-network-ready cuando la carga completa de red termina ──
  useEffect(() => {
    if (initialNetworkLoadComplete && !fullNetworkLoggedRef.current) {
      markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:full-network-ready');
      fullNetworkLoggedRef.current = true;
    }
  }, [initialNetworkLoadComplete]);

  // isLoadingScreenActive: controla el atributo data- y los estilos CSS de paneles.
  // Fase 2D.2: se vuelve false en cuanto el objeto MapLibre existe (mapShellReady ~174ms).
  // NO depende de style, tiles, backbone ni red completa.
  const isLoadingScreenActive = !mapShellReady;

  useEffect(() => {
    if (!isLoadingScreenActive && !toolsEnabledLoggedRef.current) {
      markOpeningPerfEvent(openingPerfSessionRef.current, 'ui:tools-enabled');
      toolsEnabledLoggedRef.current = true;
    }
  }, [isLoadingScreenActive]);

  // ── Hook de edición de red (FASE 4: selección unificada) ──
  const {
    editMode, previewNode, previewEdge, previewEdge2, previewEdges, confirmation,
    pendingConfirmationNode,  // UX: Node being confirmed (visible during API call)
    pendingConfirmationEdge,  // UX: Edge being confirmed (visible during API call)
    cancelEdit, confirmSegmentSplit, confirmDeleteNode, confirmDeleteEdge, startSegmentSplit,
    startBranchCreation, promptDeleteNode, promptDeleteEdge, branchSourceNodeId, setSnapTarget,
    selectedNodeId, setSelectedNodeId, selectedEdgeId, setSelectedEdgeId,
    edgeContextMenu, closeEdgeContextMenu, nodeContextMenu, closeNodeContextMenu, startInsertNodeOnEdge,
    startGeometryEdit, simplifyEdgeToLine,
    // Multi-Merge V1 (Fase 3)
    addEdgeToSelection,
  } = useNetworkEdit();

  // ── Estado para posición del cursor durante create-branch (para snap detection) ──
  // NOTA: Movido ANTES de useSnapDetection para evitar problemas de orden de hooks
  const [branchCursorPosition, setBranchCursorPosition] = useState<[number, number] | null>(null);

  // ── Ref para trackear snap target previo (para limpieza de highlight) ──
  const snapPrevTarget = useRef<{ type: string; id: string } | null>(null);

  // ── Hook de detección de snap durante create-branch ──
  // NOTA: Movido ANTES de useNetworkEditInteractivity para que éste tenga acceso al snapTarget
  const { snapTarget, calculateSnapSync } = useSnapDetection({
    enabled: editMode === 'create-branch',
    cursorPosition: branchCursorPosition,
    excludeNodeId: branchSourceNodeId,
    nodeSnapThreshold: 15, // Generoso para nodos (fácil conectar a nodos existentes)
    edgeSnapThreshold: 6,  // Reducido para edges (evitar atrapar cursor al pasar)
  });

  // ── Hook de interactividad de edición de red ──
  // FIX: Pasar calculateSnapSync para cálculo síncrono durante mousemove
  useNetworkEditInteractivity({
    enabled: isNetworkInteractive,
    currentSnapTarget: snapTarget,
    calculateSnapSync,
  });

  useCanonicalEdgeSemanticHover({
    enabled: isNetworkInteractive,
  });

  // ── Hook de edición geométrica de edges ──
  // Renderiza handles y maneja drag de shape points internos
  useEdgeGeometryEdit();

  // ── Hook para activar edición geométrica con doble-click ──
  useEdgeGeometryEditOnDoubleClick();

  // ── Hook de detección de mid-points ──
  const { midPoint, isNearMidPoint } = useMidPointDetection({
    enabled: isNetworkInteractive && !editMode, // Solo cuando no hay modo activo
    hoveredEdgeId,
    minZoom: 14,
    hoverThreshold: 20,
  });

  // ── Sincronizar snapTarget con contexto ──
  useEffect(() => {
    if (editMode === 'create-branch') {
      if (snapTarget) {
        // Hay snap activo: sincronizar al contexto
        setSnapTarget({
          type: snapTarget.type,
          id: snapTarget.id,
          snapPoint: snapTarget.snapPoint,
        });
      } else {
        // NO hay snap: limpiar para evitar snap stale (FIX: Hallazgo #1)
        setSnapTarget(null);
      }
    } else {
      // Modo diferente: siempre limpiar
      setSnapTarget(null);
    }
  }, [editMode, snapTarget, setSnapTarget]);

  // ── Cambiar cursor durante create-branch (crosshair normal, pointer cuando hay snap) ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const canvas = map.getCanvas();
    if (editMode === 'create-branch') {
      // Si hay snap activo, usar pointer para indicarlo visualmente
      if (snapTarget) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'crosshair';
      }
    } else {
      // Solo resetear si no está siendo usado por otros handlers
      if (canvas.style.cursor === 'crosshair' || canvas.style.cursor === 'pointer') {
        canvas.style.cursor = '';
      }
    }

    return () => {
      if (canvas.style.cursor === 'crosshair' || canvas.style.cursor === 'pointer') {
        canvas.style.cursor = '';
      }
    };
  }, [editMode, snapTarget]);

  // ── Feedback visual de snap (highlight del target) ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    // Limpiar highlight previo
    const prevSnapTargetRef = { current: null as { type: string; id: string } | null };

    return () => {
      // Limpiar al desmontar
      if (prevSnapTargetRef.current) {
        map.removeFeatureState(
          { source: 'canonical-network', id: prevSnapTargetRef.current.id },
          'snapped'
        );
      }
    };
  }, []);

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    // Limpiar highlight previo
    if (snapPrevTarget.current) {
      map.removeFeatureState(
        { source: 'canonical-network', id: snapPrevTarget.current.id },
        'snapped'
      );
      snapPrevTarget.current = null;
    }

    // Highlight nuevo target
    if (snapTarget) {
      map.setFeatureState(
        { source: 'canonical-network', id: snapTarget.id },
        { snapped: true }
      );
      snapPrevTarget.current = { type: snapTarget.type, id: snapTarget.id };
    }
  }, [snapTarget]);

  // ── Sincronizar preview features con el source de preview SEPARADO ──
  // NOTA: Usamos un source separado 'canonical-network-preview' para evitar problemas con filtros
  useEffect(() => {
    // ═══ PERF INSTRUMENTATION ═══
    console.log('[PERF] MapLayout preview sync triggered', {
      hasPreviewNode: !!previewNode,
      hasPendingConfirmationNode: !!pendingConfirmationNode,
      hasPreviewEdge: !!previewEdge,
      hasPendingConfirmationEdge: !!pendingConfirmationEdge,
      editMode,
      timestamp: performance.now(),
    });

    const map = mapEngine.getMap();
    if (!map) return;

    const previewSource = map.getSource('canonical-network-preview') as maplibregl.GeoJSONSource;
    if (!previewSource) return;

    // No mostrar preview visual para delete-node (solo confirmación)
    if (editMode === 'delete-node') {
      previewSource.setData({
        type: 'FeatureCollection',
        features: [],
      });
      return;
    }

    // Construir array de preview features
    const previewFeatures: GeoJSON.Feature[] = [];
    const isMoveNode = editMode === 'move-node';

    // UX IMPROVEMENT: Add pending confirmation node FIRST (renders below floating preview)
    if (pendingConfirmationNode) {
      previewFeatures.push({
        type: 'Feature',
        id: 'pending-confirmation-node',
        geometry: pendingConfirmationNode.geometry,
        properties: {
          type: 'node',
          isPending: true,  // Flag for potential style differentiation
        },
      });
      console.log('[MapLayout] ✅ Pending confirmation node added to preview');
    }

    // UX IMPROVEMENT: Add pending confirmation edge (keeps cyan line visible during API call)
    if (pendingConfirmationEdge) {
      previewFeatures.push({
        type: 'Feature',
        id: 'pending-confirmation-edge',
        geometry: pendingConfirmationEdge.geometry,
        properties: {
          type: 'edge',
          isPending: true,  // Flag for potential style differentiation
        },
      });
      console.log('[MapLayout] ✅ Pending confirmation edge added to preview');
    }

    if (previewNode) {
      previewFeatures.push({
        type: 'Feature',
        id: isMoveNode ? `preview-node-${previewNode.id}` : 'preview-node',
        geometry: previewNode.geometry,
        properties: {
          type: 'node',
        },
      });
    }

    if (isMoveNode && previewEdges && previewEdges.length > 0) {
      previewEdges.forEach((edge) => {
        previewFeatures.push({
          type: 'Feature',
          id: `preview-edge-${edge.id}`,
          geometry: edge.geometry,
          properties: {
            type: 'edge',
          },
        });
      });
    } else {
      if (previewEdge) {
        previewFeatures.push({
          type: 'Feature',
          id: 'preview-edge',
          geometry: previewEdge.geometry,
          properties: {
            type: 'edge',
          },
        });
      }

      if (previewEdge2) {
        previewFeatures.push({
          type: 'Feature',
          id: 'preview-edge-2',
          geometry: previewEdge2.geometry,
          properties: {
            type: 'edge',
          },
        });
      }
    }

    // DEBUG: Log preview sync
    console.log('[MapLayout] 🔄 Preview sync:', {
      editMode,
      hasPreviewNode: !!previewNode,
      hasPreviewEdge: !!previewEdge,
      previewFeaturesCount: previewFeatures.length,
      previewNodeCoords: previewNode?.geometry?.coordinates,
      previewEdgeCoords: previewEdge?.geometry?.coordinates,
    });

    // Actualizar el source de preview (siempre, es pequeño y rápido)
    previewSource.setData({
      type: 'FeatureCollection',
      features: previewFeatures,
    });

    // Para move-node: esconder features originales usando feature-state
    if (isMoveNode && previewNode) {
      map.setFeatureState(
        { source: 'canonical-network', id: previewNode.id },
        { hidden: true }
      );

      if (previewEdges && previewEdges.length > 0) {
        previewEdges.forEach((edge) => {
          map.setFeatureState(
            { source: 'canonical-network', id: edge.id },
            { hidden: true }
          );
        });
      }
    }
  }, [editMode, previewNode, previewEdge, previewEdge2, previewEdges, pendingConfirmationNode, pendingConfirmationEdge]);

  // ── Trackear cursor durante create-branch para snap detection ──
  useEffect(() => {
    if (editMode !== 'create-branch') {
      setBranchCursorPosition(null);
      return;
    }

    const map = mapEngine.getMap();
    if (!map) {
      return;
    }

    const handleMouseMove = (e: maplibregl.MapMouseEvent): void => {
      setBranchCursorPosition([e.lngLat.lng, e.lngLat.lat]);
    };

    map.on('mousemove', handleMouseMove);

    return () => {
      map.off('mousemove', handleMouseMove);
    };
  }, [editMode]);

  /**
   * Mapper de funciones de entidades específicas a genéricas
   * Permite usar el entity registry mientras mantenemos compatibilidad con useMapTools
   */
  const entityOperations = useMemo(() => ({
    tree: {
      add: addTree,
      update: updateTree,
      remove: removeTree,
      save: saveTree,
      select: selectTree,
      getSelected: () => trees.find(t => t.id === selectedTreeId),
      eventName: 'tree-created',
    },
    fountain: {
      add: addFountain,
      update: updateFountain,
      remove: removeFountain,
      save: saveFountain,
      select: selectFountain,
      getSelected: () => fountains.find(f => f.id === selectedFountainId),
      eventName: 'fountain-created',
    },
    container: {
      add: addContainer,
      update: updateContainer,
      remove: removeContainer,
      save: async (entity: any) => {
        console.warn('[MapLayout] Container save not implemented yet');
        return entity;
      },
      select: selectContainer,
      getSelected: () => containers.find(c => c.id === selectedContainerId),
      eventName: 'container-created',
    },
  }), [addTree, updateTree, removeTree, saveTree, selectTree, trees, selectedTreeId,
       addFountain, updateFountain, removeFountain, saveFountain, selectFountain, fountains, selectedFountainId,
       addContainer, updateContainer, removeContainer, selectContainer, containers, selectedContainerId]);

  // Hook de rutas (módulo waste) — solo auto-carga si el módulo waste está activo
  const { routes, createRoute, editRoute, error: routesError } = useRoutes({
    projectId: projectId || '',
    autoLoad: activeModule === 'waste',
  });

  // Integración de rutas con el mapa (módulo waste)
  // Las rutas están siempre visibles, independientemente del módulo activo.
  // autoLoad se limita al módulo waste para evitar fetch innecesario en arranque.
  useRoutesMapIntegration({
    projectId: projectId,
    enabled: true,
    autoLoad: activeModule === 'waste',
  });

  // Hook de grids (mallas y celdas)
  const {
    grids,
    activeGridId, setActiveGridId,
    cellRows, setCellRows,
    hoveredCellId, setHoveredCellId,
    selectedCellId, setSelectedCellId,
    cellPopoverPosition, setCellPopoverPosition,
  } = useGrids();

  // Hook de vías base
  const {
    isBaseNetworkInteractive,
    setHoveredRoadId,
    setSelectedRoadId,
  } = useBaseNetwork();

  // Malla activa (memoizada)
  const activeGrid = useMemo(() => {
    return grids.find(g => g.id === activeGridId) ?? null;
  }, [grids, activeGridId]);

  // Estado para celda cuyo detalle se está viendo (panel de detalles)
  const [detailedCellId, setDetailedCellId] = useState<string | null>(null);

  // Celda detallada (memoizada)
  const detailedCell = useMemo(() => {
    if (!detailedCellId) return null;
    return cellRows.find(row => row.h3Index === detailedCellId) ?? null;
  }, [detailedCellId, cellRows]);

  // Árbol seleccionado (memoizado para detectar cambios)
  const selectedTree = useMemo(() => {
    const tree = selectedTreeId ? trees.find((t) => t.id === selectedTreeId) : null;
    if (tree) {
      console.log('[MapLayout] ====================================');
      console.log('[MapLayout] selectedTree cambió');
      console.log('[MapLayout]   ID:', tree.id);
      console.log('[MapLayout]   Código:', tree.code);
      console.log('[MapLayout]   Nombre:', tree.commonName);
      console.log('[MapLayout] ====================================');
    }
    return tree;
  }, [selectedTreeId, trees]);

  // Fuente seleccionada (memoizado para detectar cambios)
  const selectedFountain = useMemo(() => {
    const fountain = selectedFountainId ? fountains.find((f) => f.id === selectedFountainId) : null;
    if (fountain) {
      console.log('[MapLayout] ====================================');
      console.log('[MapLayout] selectedFountain cambió');
      console.log('[MapLayout]   ID:', fountain.id);
      console.log('[MapLayout]   Código:', fountain.code);
      console.log('[MapLayout]   Nombre:', fountain.name);
      console.log('[MapLayout] ====================================');
    }
    return fountain;
  }, [selectedFountainId, fountains]);

  // Estado para el proyecto cargado
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  // Estado para la malla en edición (panel derecho)
  const [editingGrid, setEditingGrid] = useState<SpatialGrid | null>(null);

  // Estado para el escenario activo (modo Planificación)
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);

  // Estado para preview de elemento antes de crear
  const [elementPreview, setElementPreview] = useState<{
    type: 'tree' | 'park' | 'fountain' | 'container' | 'route';
    coordinates: [number, number];
  } | null>(null);

  // Ref para acceder al valor actual de elementPreview sin reregistrar handlers
  const elementPreviewRef = useRef(elementPreview);

  // Estado para dibujo manual de rutas
  const [isDrawingRoute, setIsDrawingRoute] = useState(false);
  const [routePoints, setRoutePoints] = useState<[number, number][]>([]);
  const [showRouteDrawingBanner, setShowRouteDrawingBanner] = useState(false);

  // Estado para CreateRoutePanel (módulo waste)
  const [createRoutePanelData, setCreateRoutePanelData] = useState<{
    routeId: string; // ID de la ruta ya creada en BD
    geometry: GeoJSON.LineString;
    containerIds: string[];
  } | null>(null);

  // Estado para visualización de indicadores
  const [activeIndicatorVisualization, setActiveIndicatorVisualization] = useState<{
    indicatorType: IndicatorType;
    classification: ClassificationResult;
  } | null>(null);

  // Estado de selección zonal (modo Analizar)
  const [zonalSelection, setZonalSelection] = useState<SelectedArea | null>(null);

  // Indicadores calculados (para estadísticas zonales)
  const [calculatedIndicators, setCalculatedIndicators] = useState<IndicatorResult[]>([]);

  // Estado de visualización de análisis de red urbana
  const {
    visible: hierarchyLayerVisible,
    setVisible: setHierarchyLayerVisible,
  } = useNetworkHierarchyVisibilityStore();
  const {
    analysisActive,
    activeIndicator,
    setAnalysisActive,
    setActiveIndicator,
    structureVisualRangeOverride,
    structureContrastExponent,
  } = useNetworkAnalysisUiStore();

  const [edgeDetailsInitialTab, setEdgeDetailsInitialTab] =
    useState<EdgeDetailTab>('general');
  const previousNetworkAnalysisActiveRef = useRef(false);
  const previousSelectedEdgeIdRef = useRef<string | null>(null);
  const hierarchySegmentSelectionRef = useRef(false);

  // Modo operacional (del store ambiental — comparte el mismo ModeSelector)
  const { mode: operationalMode, setMode: setOperationalMode } = useOperationalModeStore();

  // Obtener definición del módulo activo desde el registry
  // Esto permite renderizar dinámicamente el panel izquierdo sin hardcodear módulos
  const activeModuleDefinition = useModule(activeModule);

  // Hook de sintaxis espacial (fuente de verdad actual)
  const syntaxAnalysis = useSyntaxAnalysis();
  const {
    syntaxModeActive,
    selectedMetric,
    setSelectedMetric,
    setSyntaxModeActive,
  } = syntaxAnalysis;

  // Hook de visualización de sintaxis espacial
  // Aplica rampas de color a la red cuando syntaxModeActive = true
  useSyntaxVisualization();

  // Hook de visualización de población por segmento
  // Aplica rampas de color a la red cuando populationModeActive = true
  usePopulationVisualization();

  // Hook de interactividad para segmentos de población
  // Hover con bbox + click directo → EdgeDetailsPanel tab 'population'
  // Activo cuando populationModeActive = true (funciona con y sin isNetworkInteractive)
  usePopulationSegmentInteractivity({
    setSelectedEdgeId,
    setUrbanNetworkSelectedEdgeId,
    selectedEdgeId,
  });

  // Contexto de población para sincronizar populationModeActive con activeIndicator
  const { setPopulationModeActive, populationModeActive } = usePopulationSegment();

  // Hook de datos de Prioridad Estructural — solo carga cuando la capa o el análisis están activos
  const hierarchyAnalysis = useNetworkHierarchy(projectId, {
    enabled: hierarchyLayerVisible || analysisActive,
  });
  const hierarchyDataByEdgeId = useMemo(() => {
    const edgeData = new Map<string, any>();

    if (!hierarchyAnalysis.data) {
      return edgeData;
    }

    for (const feature of hierarchyAnalysis.data.features) {
      if (feature.properties?.edgeId) {
        edgeData.set(feature.properties.edgeId, feature.properties);
      }
    }

    return edgeData;
  }, [hierarchyAnalysis.data]);

  const selectedEdgeHierarchyData = useMemo(() => {
    if (!selectedEdgeId) {
      return null;
    }

    return hierarchyDataByEdgeId.get(selectedEdgeId) ?? null;
  }, [hierarchyDataByEdgeId, selectedEdgeId]);

  useEffect(() => {
    const shouldEnableAnalysis = activeModule === 'urban-network' && analysisActive;
    const shouldShowStructure  = shouldEnableAnalysis && activeIndicator === 'structure';
    const shouldShowPopulation = shouldEnableAnalysis && activeIndicator === 'population';
    const shouldShowSyntax     = shouldEnableAnalysis && activeIndicator !== 'structure' && activeIndicator !== 'population';

    if (activeIndicator !== 'structure' && activeIndicator !== 'population' && selectedMetric !== activeIndicator) {
      setSelectedMetric(activeIndicator);
    }

    if (syntaxModeActive !== shouldShowSyntax) {
      setSyntaxModeActive(shouldShowSyntax);
    }

    if (hierarchyLayerVisible !== shouldShowStructure) {
      setHierarchyLayerVisible(shouldShowStructure);
    }

    setPopulationModeActive(shouldShowPopulation);
  }, [
    activeIndicator,
    activeModule,
    analysisActive,
    hierarchyLayerVisible,
    selectedMetric,
    setHierarchyLayerVisible,
    setSelectedMetric,
    setSyntaxModeActive,
    syntaxModeActive,
    setPopulationModeActive,
  ]);

  // Hook de visualización del indicador derivado
  useNetworkHierarchyLayer({
    data: hierarchyAnalysis.data,
    visible: hierarchyLayerVisible,
    visualRange: structureVisualRangeOverride,
    contrastExponent: structureContrastExponent,
  });

  // Listener para clicks en segmentos de Prioridad Estructural
  useEffect(() => {
    const handleHierarchySegmentClick = (event: CustomEvent) => {
      const { edgeId } = event.detail;
      if (edgeId) {
        hierarchySegmentSelectionRef.current = true;
        setAnalysisActive(true);
        setActiveIndicator('structure');
        setSelectedEdgeId(edgeId);
        setUrbanNetworkSelectedEdgeId(edgeId);
        setSelectedNodeId(null);
        console.log('[MapLayout] Opening EdgeDetailsPanel from hierarchy click:', edgeId);
      }
    };

    window.addEventListener('hierarchy-segment-click', handleHierarchySegmentClick as EventListener);

    return () => {
      window.removeEventListener('hierarchy-segment-click', handleHierarchySegmentClick as EventListener);
    };
  }, [
    setActiveIndicator,
    setAnalysisActive,
    setSelectedEdgeId,
    setSelectedNodeId,
    setUrbanNetworkSelectedEdgeId,
  ]);

  const getPreferredEdgeDetailsTab = useCallback((): EdgeDetailTab => {
    if (hierarchySegmentSelectionRef.current) {
      return 'structure';
    }

    if (!analysisActive) {
      return 'general';
    }

    if (activeIndicator === 'structure')  return 'structure';
    if (activeIndicator === 'population') return 'population';
    return 'syntax';
  }, [activeIndicator, analysisActive]);

  useEffect(() => {
    if (!selectedEdgeId) {
      previousSelectedEdgeIdRef.current = null;
      hierarchySegmentSelectionRef.current = false;
      return;
    }

    if (selectedNodeId) {
      setSelectedNodeId(null);
    }

    const nextTab = getPreferredEdgeDetailsTab();

    setEdgeDetailsInitialTab(nextTab);
    if (previousSelectedEdgeIdRef.current === selectedEdgeId) {
      hierarchySegmentSelectionRef.current = false;
      return;
    }

    previousSelectedEdgeIdRef.current = selectedEdgeId;
    hierarchySegmentSelectionRef.current = false;
  }, [
    getPreferredEdgeDetailsTab,
    selectedEdgeId,
    selectedNodeId,
    setSelectedNodeId,
  ]);

  const openNetworkAnalysisOverview = useCallback(async (): Promise<boolean> => {
    if (!analysisActive) {
      return false;
    }

    const selected = await selectFeature(
      { id: 'network-analysis-overview', type: 'network-analysis' },
      'network-analysis',
    );

    if (!selected) {
      return false;
    }

    setActivePanel('network-analysis');
    return true;
  }, [analysisActive, selectFeature, setActivePanel]);

  const restoreNetworkAnalysisOverview = useCallback(async (): Promise<boolean> => {
    const restored = await openNetworkAnalysisOverview();

    if (!restored && activePanel === 'network-analysis') {
      setActivePanel(null);
    }

    return restored;
  }, [activePanel, openNetworkAnalysisOverview, setActivePanel]);

  const closeNetworkAnalysisOverview = useCallback((): void => {
    setAnalysisActive(false);
    setActivePanel(null);
    closeRightPanel();
  }, [closeRightPanel, setActivePanel, setAnalysisActive]);

  useEffect(() => {
    if (
      activePanel !== 'network-analysis' &&
      selectedFeature?.type === 'network-analysis' &&
      !selectedEdgeId &&
      !selectedNodeId
    ) {
      closeRightPanel();
    }
  }, [activePanel, closeRightPanel, selectedEdgeId, selectedFeature, selectedNodeId]);

  // Fase 2A: apertura automática del inspector analítico (NetworkAnalysisOverviewPanel)
  // al activar el modo análisis, respetando cualquier inspección contextual activa.
  useEffect(() => {
    const analysisJustActivated = analysisActive && !previousNetworkAnalysisActiveRef.current;
    const noSegmentSelection = !selectedEdgeId && !selectedNodeId;
    // Fase 2A: también respetar fichas contextuales no-analíticas (edificio, árbol,
    // contenedor, parada, celda…). selectedFeature se limpia al cerrar el panel,
    // así que null === "nada seleccionado". type:'network-analysis' es el token
    // del propio inspector analítico, no una inspección de entidad.
    const noContextualFeature =
      !selectedFeature || selectedFeature?.type === 'network-analysis';

    const syncOverviewPanel = async (): Promise<void> => {
      if (activeModule !== 'urban-network') {
        if (activePanel === 'network-analysis') {
          setActivePanel(null);
          closeRightPanel();
        }
        previousNetworkAnalysisActiveRef.current = analysisActive;
        return;
      }

      if (!analysisActive) {
        if (activePanel === 'network-analysis') {
          setActivePanel(null);
          closeRightPanel();
        }
        previousNetworkAnalysisActiveRef.current = false;
        return;
      }

      // Fase 2A: respetar cualquier selección activa — segmento, nodo, o entidad
      // contextual — sin pisarla al activar el análisis.
      if (!noSegmentSelection || !noContextualFeature) {
        previousNetworkAnalysisActiveRef.current = analysisActive;
        return;
      }

      if (analysisJustActivated) {
        if (activePanel && activePanel !== 'network-analysis') {
          const canChange = await canChangeContent();
          if (!canChange) {
            previousNetworkAnalysisActiveRef.current = analysisActive;
            return;
          }
        }

        await openNetworkAnalysisOverview();
      }

      previousNetworkAnalysisActiveRef.current = analysisActive;
    };

    void syncOverviewPanel();
  }, [
    analysisActive,
    activeModule,
    activePanel,
    canChangeContent,
    closeRightPanel,
    openNetworkAnalysisOverview,
    selectedEdgeId,
    selectedFeature, // Fase 2A: guard contra fichas contextuales no-analíticas
    selectedNodeId,
    setActivePanel,
  ]);

  /**
   * Handlers para interacción con celdas de malla
   *
   * @remarks
   * Estos handlers se comparten entre GridsPanel y GridCellsPanel
   * para mantener la sincronización entre el mapa y las tablas.
   */

  // Refs para evitar re-renders innecesarios (celdas)
  const hoveredCellIdRef = useRef<string | null>(null);
  const selectedCellIdRef = useRef<string | null>(null);
  const activeGridIdRef = useRef<string | null>(null);
  useEffect(() => { activeGridIdRef.current = activeGridId; }, [activeGridId]);

  // Refs para evitar re-renders innecesarios (vías base)
  const hoveredRoadIdRef = useRef<string | null>(null);
  const selectedRoadIdRef = useRef<string | null>(null);

  /**
   * Callback para hover desde la tabla → actualiza mapa con setFeatureState O(1).
   */
  const handleCellHover = useCallback((h3Index: string | null): void => {
    const id = activeGridIdRef.current;
    const map = mapEngine.getMap();
    if (map && id) {
      const src = `h3-grid-source-${id}`;
      if (map.getSource(src)) {
        if (hoveredCellIdRef.current) map.setFeatureState({ source: src, id: hoveredCellIdRef.current }, { hovered: false });
        if (h3Index)               map.setFeatureState({ source: src, id: h3Index }, { hovered: true });
      }
    }
    hoveredCellIdRef.current = h3Index;
    setHoveredCellId(h3Index);
  }, [setHoveredCellId]);

  /**
   * Callback para selección desde la tabla → actualiza mapa con setFeatureState.
   */
  const handleCellSelect = useCallback((h3Index: string | null): void => {
    const id = activeGridIdRef.current;
    const map = mapEngine.getMap();
    if (map && id) {
      const src = `h3-grid-source-${id}`;
      if (map.getSource(src)) {
        if (selectedCellIdRef.current) {
          map.setFeatureState({ source: src, id: selectedCellIdRef.current }, { selected: false });
        }
        if (h3Index) {
          map.setFeatureState({ source: src, id: h3Index }, { selected: true });
        }
      }
    }
    selectedCellIdRef.current = h3Index;
    setSelectedCellId(h3Index);
  }, [setSelectedCellId]);

  /** Guarda el nombre personalizado de una celda. */
  const handleCellNameChange = useCallback(async (h3Index: string, name: string): Promise<void> => {
    if (!activeGridId) return;
    try {
      await gridApi.setCellName(activeGridId, h3Index, name);
      // Actualizar en GridsContext
      setCellRows(prev => prev.map(r =>
        r.h3Index === h3Index ? { ...r, cellName: name.trim() || null } : r
      ));
    } catch {
      // silently ignore
    }
  }, [activeGridId, setCellRows]);

  /**
   * Handlers para interacción con vías base
   *
   * @remarks
   * Similares a handleCellHover/handleCellSelect pero para vías.
   * Actualizan el mapa con setFeatureState() O(1).
   */

  /**
   * Callback para hover desde la tabla de vías → actualiza mapa con setFeatureState
   */
  const handleRoadHover = useCallback((roadId: string | null): void => {
    const map = mapEngine.getMap();
    if (map && map.getSource('base-road-network-source')) {
      // Limpiar hover anterior
      if (hoveredRoadIdRef.current) {
        map.setFeatureState(
          { source: 'base-road-network-source', id: hoveredRoadIdRef.current },
          { hovered: false }
        );
      }
      // Aplicar nuevo hover
      if (roadId) {
        map.setFeatureState(
          { source: 'base-road-network-source', id: roadId },
          { hovered: true }
        );
      }
    }
    hoveredRoadIdRef.current = roadId;
    setHoveredRoadId(roadId);
  }, [setHoveredRoadId]);

  /**
   * Callback para selección desde la tabla de vías → actualiza mapa con setFeatureState
   */
  const handleRoadSelect = useCallback((roadId: string | null): void => {
    const map = mapEngine.getMap();
    if (map && map.getSource('base-road-network-source')) {
      // Limpiar selección anterior
      if (selectedRoadIdRef.current) {
        map.setFeatureState(
          { source: 'base-road-network-source', id: selectedRoadIdRef.current },
          { selected: false }
        );
      }
      // Aplicar nueva selección
      if (roadId) {
        map.setFeatureState(
          { source: 'base-road-network-source', id: roadId },
          { selected: true }
        );
      }
    }
    selectedRoadIdRef.current = roadId;
    setSelectedRoadId(roadId);
  }, [setSelectedRoadId]);

  /** Vuela al centro de una celda en el mapa. */
  const handleZoomToCell = useCallback((h3Index: string): void => {
    // Implementación simplificada - GridsPanel tiene una versión más completa
    // que usa el geojsonCache. Por ahora, este es un placeholder.
    console.log('[MapLayout] Zoom to cell:', h3Index);
  }, []);

  /**
   * Handler para cuando se hace clic en el ícono de estadísticas de una celda.
   * Abre el panel de detalles de la celda.
   */
  const handleCellStatsClick = useCallback((h3Index: string): void => {
    console.log('[MapLayout] Opening cell details for:', h3Index);
    setDetailedCellId(h3Index);
  }, []);

  /**
   * Gestiona la interactividad de vías base
   *
   * @remarks
   * Añade/remueve event listeners cuando isBaseNetworkInteractive cambia.
   * Permite click/hover en vías del mapa para sincronizar con el panel.
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !isBaseNetworkInteractive) {
      // Limpiar listeners si la interactividad está desactivada
      return;
    }

    const layerIds = [
      'base-road-network-layer-autopista_010',
      'base-road-network-layer-autopista_050',
      'base-road-network-layer-nacional',
      'base-road-network-layer-provincial',
      'base-road-network-layer-terciaria',
    ];

    // Handler para mousemove (hover)
    const handleMouseMove = (e: any): void => {
      if (e.features && e.features.length > 0) {
        const roadId = e.features[0].id as string;
        handleRoadHover(roadId);
        map.getCanvas().style.cursor = 'pointer';
      }
    };

    // Handler para mouseleave
    const handleMouseLeave = (): void => {
      handleRoadHover(null);
      map.getCanvas().style.cursor = '';
    };

    // Handler para click
    const handleClick = (e: any): void => {
      if (e.features && e.features.length > 0) {
        const roadId = e.features[0].id as string;
        handleRoadSelect(roadId);
      }
    };

    // Añadir listeners a todas las capas
    layerIds.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.on('mousemove', layerId, handleMouseMove);
        map.on('mouseleave', layerId, handleMouseLeave);
        map.on('click', layerId, handleClick);
      }
    });

    console.log('[MapLayout] Interactividad de vías base activada');

    // Cleanup: remover listeners
    return () => {
      layerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.off('mousemove', layerId, handleMouseMove);
          map.off('mouseleave', layerId, handleMouseLeave);
          map.off('click', layerId, handleClick);
        }
      });
      map.getCanvas().style.cursor = '';
      console.log('[MapLayout] Interactividad de vías base desactivada');
    };
  }, [isBaseNetworkInteractive, handleRoadHover, handleRoadSelect]);

  /**
   * Cargar proyecto actual y configurar módulos disponibles
   *
   * @remarks
   * Los módulos disponibles se obtienen del proyecto.activeModules.
   * Solo se muestran los módulos que el proyecto tiene contratados.
   */
  useEffect(() => {
    if (!projectId) {
      console.warn('[MapLayout] No hay projectId configurado');
      setAvailableModules([]);
      return;
    }

    const openingPerfSessionId = openingPerfSessionRef.current;

    // Resolver proyecto: preferir contexto (evita 404 cross-tenant para superadmin)
    const resolveProject = async (): Promise<Project | null> => {
      const fromContext = availableProjects.find(p => p.id === projectId);
      if (fromContext) {
        console.log('[🔍 BOOTSTRAP] MapLayout usando proyecto desde ProjectContext:', {
          projectId,
          projectName: fromContext.name,
          timestamp: new Date().toISOString(),
        });
        markOpeningPerfEvent(openingPerfSessionId, 'project:detail-fetch-end');
        return fromContext;
      }

      // Fallback: fetch directo (usuario regular con proyecto de su tenant)
      try {
        console.log('[🔍 BOOTSTRAP] MapLayout fallback fetch proyecto:', {
          projectId,
          timestamp: new Date().toISOString(),
        });
        markOpeningPerfEvent(openingPerfSessionId, 'project:detail-fetch-start');
        const fetched = await projectsApi.get(projectId);
        markOpeningPerfEvent(openingPerfSessionId, 'project:detail-fetch-end');
        return fetched;
      } catch (error: any) {
        console.error('[🔍 BOOTSTRAP] MapLayout error al cargar proyecto:', {
          projectId,
          status: error?.response?.status,
          message: error?.message || String(error),
          timestamp: new Date().toISOString(),
        });
        return null;
      }
    };

    const loadProject = async () => {
      const project = await resolveProject();
      if (!project) {
        setAvailableModules([]);
        return;
      }

      const hasBounds = project.bounds &&
        typeof project.bounds.minLng === 'number' &&
        typeof project.bounds.minLat === 'number' &&
        typeof project.bounds.maxLng === 'number' &&
        typeof project.bounds.maxLat === 'number';

      if (hasBounds) {
        markOpeningPerfEvent(openingPerfSessionId, 'project:bounds-ready');
        writeProjectBootstrapCache({
          id: projectId,
          name: project.name,
          bounds: project.bounds ?? undefined,
          activeModules: (project.activeModules ?? []) as string[],
        });
        console.log('[project-bootstrap-cache] write', { projectId });
        markOpeningPerfEvent(openingPerfSessionId, 'project-bootstrap-cache:write');
      } else {
        invalidateProjectBootstrapCache(projectId);
        markOpeningPerfEvent(openingPerfSessionId, 'project-bootstrap-cache:invalid');
      }

      setCurrentProject(project);

      console.log('[🔍 BOOTSTRAP] MapLayout proyecto resuelto:', {
        projectId,
        projectName: project.name,
        hasBounds,
        timestamp: new Date().toISOString(),
      });

      // Extraer módulos activos del proyecto
      let activeModules = (project.activeModules || []) as string[];

      // Mapear módulos legacy a nuevos IDs
      const MODULE_MAPPING: Record<string, ModuleId> = {
        'vias': 'urban-network',
      };
      activeModules = activeModules.map(moduleId => MODULE_MAPPING[moduleId] || moduleId) as ModuleId[];

      // Si el proyecto no tiene módulos configurados, mostrar todos (fallback)
      if (activeModules.length === 0) {
        console.warn('[MapLayout] Proyecto sin módulos configurados. Mostrando todos como fallback.');
        const allModules: ModuleId[] = [
          'urban-inventory',
          'roadways',
          'infrastructure',
          'services',
          'transit',
          'transport',
          'waste',
          'safety',
          'urban',
          'environmental',
          'social',
          'urban-network',
        ];
        setAvailableModules(allModules);
      } else {
        const withInventory: ModuleId[] = activeModules.includes('urban-inventory')
          ? (activeModules as ModuleId[])
          : ['urban-inventory', ...(activeModules as ModuleId[])];
        setAvailableModules(withInventory);
      }

      console.log('[MapLayout] Módulos disponibles del proyecto (después de mapping):', activeModules);
    };

    void loadProject();
  }, [projectId, availableProjects, setAvailableModules]);

  /**
   * Log de bounds del proyecto cuando se carga
   *
   * @remarks
   * El mapa solo se renderiza si el proyecto tiene bounds válidos.
   * Si no hay bounds, el mapa no se muestra (el usuario ya fue redirigido a /no-projects).
   */
  useEffect(() => {
    // Early return si el proyecto aún no está cargado (evita falso positivo)
    if (!currentProject) {
      return;
    }

    // Log de bounds del proyecto (fitBounds se ejecuta en MapViewer al cargar el mapa)
    const projectBounds = currentProject.bounds;
    const hasValidBounds = projectBounds &&
      typeof projectBounds.minLng === 'number' &&
      typeof projectBounds.minLat === 'number' &&
      typeof projectBounds.maxLng === 'number' &&
      typeof projectBounds.maxLat === 'number';

    if (!hasValidBounds) {
      console.warn('[MapLayout] ⚠️ Proyecto sin bounds válidos. El mapa no se renderizará.');
    } else {
      console.log('[MapLayout] ✓ Proyecto cargado con bounds:', projectBounds);
    }
  }, [currentProject]);

  /**
   * Fase 2C: Reconciliar bounds reales con cache cuando llegan del API.
   *
   * Si el mapa fue creado a partir del cache de bootstrap y los bounds reales
   * difieren significativamente, hace un fitBounds suave. Si son iguales, no hace nada.
   * Esto evita recrear el mapa innecesariamente cuando llega project:detail-fetch-end.
   */
  useEffect(() => {
    if (!currentProject) return;
    const sessionId = openingPerfSessionRef.current;

    if (!mapCreatedFromCacheRef.current || !cachedBootstrap) {
      // Mapa creado con datos reales desde el inicio — sin reconciliación necesaria
      return;
    }

    // Reconciliar una sola vez
    mapCreatedFromCacheRef.current = false;

    const real = currentProject.bounds;
    if (!real) return;

    const cached = cachedBootstrap.bounds;
    const EPSILON = 0.001; // ~100m — diferencia que justifica un refitBounds
    const boundsChanged =
      Math.abs(real.minLng - cached.minLng) > EPSILON ||
      Math.abs(real.minLat - cached.minLat) > EPSILON ||
      Math.abs(real.maxLng - cached.maxLng) > EPSILON ||
      Math.abs(real.maxLat - cached.maxLat) > EPSILON;

    if (boundsChanged) {
      console.log('[map:fitbounds-from-real-project] Bounds del proyecto cambiaron — aplicando fitBounds suave', {
        cached,
        real,
      });
      markOpeningPerfEvent(sessionId, 'map:fitbounds-from-real-project');
      const map = mapEngine.getMap();
      if (map) {
        map.fitBounds(
          [[real.minLng, real.minLat], [real.maxLng, real.maxLat]],
          { animate: true, padding: 40 }
        );
      }
    } else {
      console.log('[map:reconcile-project-detail] Bounds coinciden con cache — sin fitBounds necesario');
      markOpeningPerfEvent(sessionId, 'map:reconcile-project-detail');
    }
  }, [currentProject, cachedBootstrap]);

  /**
   * Abrir panel derecho cuando se selecciona un árbol
   *
   * NOTA: Este useEffect está deshabilitado porque TreesPanel maneja directamente
   * el clic en códigos con handleCodeClick(), que ya llama a selectTree() y
   * setActivePanel(). Este useEffect causaba que se llamara selectFeature() de
   * usePanelCoordination, lo cual ejecutaba safeClose() y cerraba el panel
   * inmediatamente después de abrirlo.
   *
   * Si en el futuro necesitas abrir el panel desde el mapa (no desde el sidebar),
   * deberás manejar ese caso de forma diferente para evitar conflictos.
  */
  /*
  useEffect(() => {
    if (selectedTreeId) {
      const tree = trees.find((t) => t.id === selectedTreeId);
      if (tree) {
        void selectFeature(tree, 'trees');
      }
    }
  }, [selectedTreeId, trees, selectFeature]);
  */

  /**
   * Abrir panel derecho cuando se selecciona un contenedor
   *
   * NOTA: Este useEffect también está deshabilitado por la misma razón que el de árboles.
   * Cuando se implemente handleCodeClick() en el módulo Waste, ese handler manejará
   * directamente la apertura del panel sin necesitar este useEffect.
   */
  /*
  useEffect(() => {
    if (selectedContainerId) {
      const container = containers.find((c) => c.id === selectedContainerId);
      if (container) {
        void selectFeature(container, 'containers');
      }
    }
  }, [selectedContainerId, containers, selectFeature]);
  */

  useEffect(() => {
    const handleTreeSelect = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<{ treeId?: string; elementId?: string }>).detail;
      const treeId = detail?.treeId ?? detail?.elementId;

      if (!treeId || selectedTreeId === treeId) {
        return;
      }

      const tree = trees.find((candidate) => candidate.id === treeId);
      if (!tree) {
        console.warn('[MapLayout] Árbol no encontrado para tree-select:', treeId);
        return;
      }

      const canChange = await selectFeature(tree, 'trees');
      if (!canChange) {
        return;
      }

      selectTree(treeId);
    };

    window.addEventListener('tree-select', handleTreeSelect);
    return () => window.removeEventListener('tree-select', handleTreeSelect);
  }, [selectFeature, selectTree, selectedTreeId, trees]);

  useEffect(() => {
    const handleWasteContainerSelect = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<{ containerId?: string; elementId?: string }>).detail;
      const containerId = detail?.containerId ?? detail?.elementId;

      if (!containerId || selectedWasteContainer?.id === containerId) {
        return;
      }

      try {
        const container = await wasteApi.containers.getById(containerId);
        const canChange = await selectFeature(container, 'waste-containers');

        if (!canChange) {
          return;
        }

        setSelectedWasteContainer(container);
      } catch (error) {
        console.error('[MapLayout] Error abriendo detalles del contenedor Waste:', error);
      }
    };

    const handleWasteContainerUpdated = (event: Event): void => {
      const updatedContainer = (event as CustomEvent<WasteContainer>).detail;
      setSelectedWasteContainer((current) =>
        current?.id === updatedContainer.id ? updatedContainer : current,
      );
    };

    const handleWasteContainerDeleted = (event: Event): void => {
      const { containerId } = (event as CustomEvent<{ containerId: string }>).detail;

      if (selectedWasteContainer?.id === containerId) {
        setSelectedWasteContainer(null);
        closeRightPanel();
      }
    };

    window.addEventListener('waste-container-select', handleWasteContainerSelect);
    window.addEventListener('waste-containers-select', handleWasteContainerSelect);
    window.addEventListener('container-updated', handleWasteContainerUpdated);
    window.addEventListener('container-deleted', handleWasteContainerDeleted);

    return () => {
      window.removeEventListener('waste-container-select', handleWasteContainerSelect);
      window.removeEventListener('waste-containers-select', handleWasteContainerSelect);
      window.removeEventListener('container-updated', handleWasteContainerUpdated);
      window.removeEventListener('container-deleted', handleWasteContainerDeleted);
    };
  }, [closeRightPanel, selectFeature, selectedWasteContainer?.id]);

  useEffect(() => {
    const selectedFeatureId = getSelectableFeatureId(selectedFeature);

    if (selectedWasteContainer && selectedFeatureId !== selectedWasteContainer.id) {
      setSelectedWasteContainer(null);
    }
    if (selectedWasteDepot && selectedFeatureId !== selectedWasteDepot.id) {
      setSelectedWasteDepot(null);
    }
    if (selectedWasteHotspot && selectedFeatureId !== selectedWasteHotspot.id) {
      setSelectedWasteHotspot(null);
    }
    if (selectedTransitStop && selectedFeatureId !== selectedTransitStop.id) {
      setSelectedTransitStop(null);
    }
  }, [selectedFeature, selectedTransitStop, selectedWasteContainer, selectedWasteDepot, selectedWasteHotspot]);

  useEffect(() => {
    setSelectedWasteContainer(null);
    setSelectedWasteDepot(null);
    setSelectedWasteHotspot(null);
    setSelectedTransitStop(null);
  }, [projectId]);

  useEffect(() => {
    const handleWasteDepotSelect = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<{ depotId?: string; elementId?: string }>).detail;
      const depotId = detail?.depotId ?? detail?.elementId;

      if (!depotId || selectedWasteDepot?.id === depotId) {
        return;
      }

      try {
        const depot = await wasteApi.depots.getById(depotId);
        const canChange = await selectFeature(depot, 'waste-depots');
        if (!canChange) {
          return;
        }
        setSelectedWasteDepot(depot);
      } catch (error) {
        console.error('[MapLayout] Error abriendo detalles del depósito Waste:', error);
      }
    };

    const handleWasteHotspotSelect = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<{ hotspotId?: string; elementId?: string }>).detail;
      const hotspotId = detail?.hotspotId ?? detail?.elementId;

      if (!hotspotId || selectedWasteHotspot?.id === hotspotId) {
        return;
      }

      try {
        const hotspot = await wasteApi.hotspots.getById(hotspotId);
        const canChange = await selectFeature(hotspot, 'waste-hotspots');
        if (!canChange) {
          return;
        }
        setSelectedWasteHotspot(hotspot);
      } catch (error) {
        console.error('[MapLayout] Error abriendo detalles del microbasural Waste:', error);
      }
    };

    const handleTransitStopSelect = async (event: Event): Promise<void> => {
      const detail = (event as CustomEvent<{ stopId?: string; elementId?: string }>).detail;
      const stopId = detail?.stopId ?? detail?.elementId;

      if (!stopId || selectedTransitStop?.id === stopId) {
        return;
      }

      try {
        const stop = await mobilityApi.stops.getById(stopId);
        const canChange = await selectFeature(stop, 'mobility-stops');
        if (!canChange) {
          return;
        }
        setSelectedTransitStop(stop);
      } catch (error) {
        console.error('[MapLayout] Error abriendo detalles de la parada:', error);
      }
    };

    window.addEventListener('waste-depot-select', handleWasteDepotSelect);
    window.addEventListener('waste-hotspot-select', handleWasteHotspotSelect);
    window.addEventListener('mobility-stops-select', handleTransitStopSelect);

    return () => {
      window.removeEventListener('waste-depot-select', handleWasteDepotSelect);
      window.removeEventListener('waste-hotspot-select', handleWasteHotspotSelect);
      window.removeEventListener('mobility-stops-select', handleTransitStopSelect);
    };
  }, [
    selectFeature,
    selectedTransitStop?.id,
    selectedWasteDepot?.id,
    selectedWasteHotspot?.id,
  ]);

  /**
   * Escucha el evento grid-edit-start para abrir el panel de edición de malla
   */
  useEffect(() => {
    const handler = async (e: Event): Promise<void> => {
      const grid = (e as CustomEvent<SpatialGrid>).detail;
      console.log('[MapLayout] Evento grid-edit-start recibido:', grid.id);

      // PRIMERO verificar si podemos cambiar (chequea cambios sin guardar)
      const canChange = await selectFeature(grid, 'grid-edit');

      // SOLO actualizar editingGrid si selectFeature tuvo éxito
      if (canChange) {
        console.log('[MapLayout] Cambiando a editingGrid:', grid.id);
        setEditingGrid(grid);
      } else {
        console.log('[MapLayout] Cambio cancelado, manteniendo editingGrid actual');
      }
    };
    window.addEventListener('grid-edit-start', handler);
    return () => window.removeEventListener('grid-edit-start', handler);
  }, [selectFeature]);

  /**
   * Escucha eventos de activación/desactivación de escenarios (modo Planificación)
   */
  useEffect(() => {
    const onActivated = (e: Event): void => {
      setActiveScenario((e as CustomEvent<Scenario>).detail);
    };
    const onDeactivated = (): void => {
      setActiveScenario(null);
    };
    window.addEventListener('scenario-activated', onActivated);
    window.addEventListener('scenario-deactivated', onDeactivated);
    return () => {
      window.removeEventListener('scenario-activated', onActivated);
      window.removeEventListener('scenario-deactivated', onDeactivated);
    };
  }, []);

  /**
   * Escucha eventos de visualización de indicadores (modo Analizar)
   */
  useEffect(() => {
    const onVisualizationUpdated = (e: Event): void => {
      const detail = (e as CustomEvent<{
        indicatorType: IndicatorType;
        classification: ClassificationResult;
      }>).detail;

      console.log('[MapLayout] Visualización de indicador actualizada:', detail.indicatorType);
      setActiveIndicatorVisualization(detail);
    };

    window.addEventListener('indicator-visualization-updated', onVisualizationUpdated);
    return () => {
      window.removeEventListener('indicator-visualization-updated', onVisualizationUpdated);
    };
  }, []);

  /**
   * Escucha eventos de selección zonal (modo Analizar)
   */
  useEffect(() => {
    const onZonalSelectionCompleted = (e: Event): void => {
      const detail = (e as CustomEvent<SelectedArea>).detail;
      console.log('[MapLayout] Selección zonal completada:', detail);
      setZonalSelection(detail);
    };

    const onZonalSelectionCleared = (): void => {
      console.log('[MapLayout] Selección zonal limpiada');
      setZonalSelection(null);
    };

    const onIndicatorCalculated = (e: Event): void => {
      const detail = (e as CustomEvent<IndicatorResult>).detail;
      console.log('[MapLayout] Indicador calculado:', detail.type);

      // Agregar o actualizar indicador en la lista
      setCalculatedIndicators((prev) => {
        const existingIndex = prev.findIndex((ind) => ind.type === detail.type);
        if (existingIndex >= 0) {
          // Reemplazar indicador existente
          const updated = [...prev];
          updated[existingIndex] = detail;
          return updated;
        } else {
          // Agregar nuevo indicador
          return [...prev, detail];
        }
      });
    };

    window.addEventListener('zonal-selection-completed', onZonalSelectionCompleted);
    window.addEventListener('zonal-selection-cleared', onZonalSelectionCleared);
    window.addEventListener('indicator-calculated', onIndicatorCalculated);

    return () => {
      window.removeEventListener('zonal-selection-completed', onZonalSelectionCompleted);
      window.removeEventListener('zonal-selection-cleared', onZonalSelectionCleared);
      window.removeEventListener('indicator-calculated', onIndicatorCalculated);
    };
  }, []);

  /**
   * Integración con DrawingService para dibujo de rutas (módulo Waste)
   *
   * @remarks
   * NOTA: Desactivado temporalmente. El dibujo de rutas ahora se maneja
   * manualmente en handleMapClick para tener control sobre el popover de
   * confirmación del primer punto (similar al flujo de árboles).
   */
  // useEffect(() => {
  //   const map = mapEngine.getMap();
  //   if (!map || activeTool !== 'draw-route') {
  //     return;
  //   }
  //
  //   console.log('[MapLayout] Inicializando DrawingService para draw-route');
  //
  //   // Obtener instancia del DrawingService
  //   const drawingService = DrawingService.getInstance();
  //
  //   // Inicializar con configuración de Waste
  //   drawingService.initialize(map, WASTE_DRAWING_CONFIG);
  //
  //   // Activar modo línea
  //   drawingService.setMode('line');
  //
  //   // Cleanup: desactivar drawing cuando se cambie de herramienta
  //   return () => {
  //     console.log('[MapLayout] Desactivando DrawingService');
  //     drawingService.setMode(null);
  //   };
  // }, [activeTool]);

  /**
   * Maneja el cambio de herramienta con verificación de cambios sin guardar
   */
  const handleToolChange = async (tool: string | null): Promise<void> => {
    // Si hay un panel abierto, verificar cambios
    if (operationalMode === 'plan' || selectedTreeId || editingGrid) {
      const canClose = await safeClose();
      if (!canClose) {
        return; // Usuario canceló, no cambiar herramienta
      }

      // Cerrar panel actual antes de cambiar
      if (operationalMode === 'plan') {
        setOperationalMode('explore');
        closeRightPanel();
      }
      if (editingGrid) {
        setEditingGrid(null);
        closeRightPanel();
      }
      if (selectedTreeId) {
        // Solo cerrar el panel y deseleccionar
        // La eliminación de árboles nuevos se maneja en TreeDetailsPanel.onCancel
        selectTree(null);
        closeRightPanel();
      }
    }

    // Si se está desactivando la herramienta (tool === null) y hay popover abierto, cerrarlo
    if (tool === null && elementPreview !== null) {
      setElementPreview(null);
    }

    // Cambiar herramienta
    setActiveTool(tool);

    // Quitar foco de cualquier elemento para que eventos keyboard vayan al document
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  /**
   * Maneja el click en el mapa para crear elementos (árboles, contenedores, etc.)
   */
  const handleMapClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    // BLOQUEO: Si hay edición activa, prevenir clicks en el mapa
    if (isEditingActive && hasUnsavedChanges) {
      console.log('[MapLayout] Click bloqueado: hay edición activa');
      alert('Debe guardar o cancelar los cambios antes de interactuar con el mapa.');
      return;
    }

    // Obtener la instancia del mapa primero
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[MapLayout] No se pudo obtener la instancia del mapa');
      return;
    }

    // Obtener coordenadas del click
    const mapContainer = event.currentTarget;
    const rect = mapContainer.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const lngLat = map.unproject([x, y]);
    const coordinates: [number, number] = [lngLat.lng, lngLat.lat];

    // Si estamos dibujando una ruta, agregar punto adicional
    if (isDrawingRoute) {
      console.log('[MapLayout] Agregando punto a ruta:', coordinates);
      setRoutePoints((prev) => [...prev, coordinates]);
      return; // No continuar con el flujo normal
    }

    // ══ EDICIÓN DE RED CANÓNICA ══
    // (Ahora manejado por map.on('click') en useNetworkEditInteractivity)

    // Determinar tipo de elemento a crear según herramienta activa
    let elementType: 'tree' | 'container' | 'route' | null = null;
    if (activeTool === 'create-tree') {
      elementType = 'tree';
    } else if (activeTool === 'add-container') {
      elementType = 'container';
    } else if (activeTool === 'draw-route') {
      elementType = 'route';
    }

    // Solo procesar si hay una herramienta de creación activa
    if (!elementType) return;

    // GUARDIA DE SEGURIDAD: No permitir creación de elementos en modo visualize (read-only)
    const { mode } = useOperationalModeStore.getState();
    if (mode === 'explore') {
      console.warn('[MapLayout] Cannot create elements in visualize mode (read-only)');
      return;
    }

    // Mostrar preview del elemento antes de crear
    setElementPreview({
      type: elementType,
      coordinates,
    });

    console.log(`[MapLayout] Preview de ${elementType} mostrado en:`, coordinates);
  };

  /**
   * Confirma la creación del elemento desde el preview
   */
  /**
   * Maneja la creación de entidades de forma genérica usando el entity registry
   */
  const handleConfirmCreate = (): void => {
    if (!elementPreview) return;

    const { type: elementType, coordinates } = elementPreview;

    // Caso especial: rutas (geometría de línea, no punto)
    if (elementType === 'route') {
      // Crear ruta en BD inmediatamente con valores por defecto
      console.log('[MapLayout] Creando ruta en BD desde:', coordinates);

      if (!projectId) {
        console.error('[MapLayout] No hay proyecto activo, no se puede crear ruta');
        alert('No hay proyecto activo');
        return;
      }

      // Crear geometría LineString inicial (con punto duplicado para ser válida)
      const initialGeometry: GeoJSON.LineString = {
        type: 'LineString',
        coordinates: [coordinates, coordinates], // Punto duplicado para tener al menos 2 puntos
      };

      // Auto-generar código de ruta
      const routeCode = `RT-${String(routes.length + 1).padStart(3, '0')}`;

      // DTO de creación
      const routeDto = {
        code: routeCode,
        name: `Nueva ruta ${routes.length + 1}`,
        geometry: initialGeometry,
        containerIds: [],
        frequency: 'daily' as const,
        estimatedDuration: 0,
        estimatedDistance: 0,
        operatingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI'] as DayOfWeek[],
        startTime: '08:00',
        status: 'active' as const,
      };

      console.log('[MapLayout] 🚀 Creando ruta en BD...');
      console.log('[MapLayout] ProjectId:', projectId);
      console.log('[MapLayout] DTO:', routeDto);

      // Crear ruta en BD con valores por defecto
      createRoute(routeDto)
        .then((createdRoute) => {
          if (!createdRoute) {
            const errorMsg = routesError || 'Error desconocido al crear ruta';

            console.error('[MapLayout] ❌ Error al crear ruta en BD');
            console.error('[MapLayout] Error:', routesError);
            console.error('[MapLayout] Error final:', errorMsg);
            console.error('[MapLayout] DTO enviado:', routeDto);
            console.error('[MapLayout] ProjectId:', projectId);

            alert(`Error al crear ruta: ${errorMsg}`);
            return;
          }

          console.log('[MapLayout] Ruta creada en BD:', createdRoute.id);

          // Activar modo de dibujo
          setIsDrawingRoute(true);

          // Agregar primer punto
          setRoutePoints([coordinates]);

          // Abrir CreateRoutePanel con el ID de la ruta ya creada
          setCreateRoutePanelData({
            routeId: createdRoute.id,
            geometry: initialGeometry,
            containerIds: [],
          });

          // Mostrar banner de instrucciones
          setShowRouteDrawingBanner(true);

          console.log('[MapLayout] Panel y banner de dibujo abiertos. Ruta ID:', createdRoute.id);

          // Dispatch evento para actualizar lista de rutas
          window.dispatchEvent(new CustomEvent('route-created'));
        })
        .catch((err) => {
          console.error('[MapLayout] Error al crear ruta:', err);
          alert('Error al crear ruta. Por favor intenta nuevamente.');
        });

      // Limpiar preview para rutas
      setElementPreview(null);
      return;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Manejo genérico de entidades usando Entity Registry
    // ═══════════════════════════════════════════════════════════════════════

    // Obtener configuración de la entidad del registry
    const entityConfig = getEntityConfig(elementType);
    if (!entityConfig) {
      console.error(`[MapLayout] No se encontró configuración para entidad: ${elementType}`);
      setElementPreview(null);
      return;
    }

    // Obtener operaciones específicas del tipo (mapper)
    const operations = entityOperations[elementType as keyof typeof entityOperations];
    if (!operations) {
      console.error(`[MapLayout] No se encontraron operaciones para entidad: ${elementType}`);
      setElementPreview(null);
      return;
    }

    let createdEntity: any = null;

    // Crear comando de undo/redo genérico
    const createCommand: UndoCommand = {
      type: 'create',
      elementType,
      data: { coordinates },
      description: `Crear ${entityConfig.displayName} en (${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)})`,
      execute: () => {
        // Crear la entidad localmente (sin seleccionar automáticamente)
        createdEntity = operations.add(coordinates, false);

        // Guardar automáticamente en BD si hay proyecto activo
        if (projectId) {
          operations.save(createdEntity, projectId)
            .then((savedEntity) => {
              // Marcar como "recién creada" usando el sistema genérico de tracking
              const entityId = entityConfig.getId!(savedEntity);
              trackNewEntity(elementType, entityId);

              // Seleccionar la entidad DESPUÉS de guardarla (ya tiene ID de BD)
              // Esto abre el panel derecho para que el usuario pueda caracterizarla
              operations.select(entityId);

              // Disparar evento para actualizar listas
              window.dispatchEvent(new CustomEvent(operations.eventName));
            })
            .catch((error) => {
              console.error(`[MapLayout] Error al guardar ${entityConfig.displayName} automáticamente:`, error);

              // Si falla el guardado, seleccionar con ID temporal
              const entityId = entityConfig.getId!(createdEntity);
              operations.select(entityId);

              // Disparar evento de todas formas
              window.dispatchEvent(new CustomEvent(operations.eventName));
            });
        } else {
          console.warn(`[MapLayout] No hay proyecto activo. El ${entityConfig.displayName} no se guardará en BD.`);

          // Sin proyecto, seleccionar con ID temporal
          const entityId = entityConfig.getId!(createdEntity);
          operations.select(entityId);

          window.dispatchEvent(new CustomEvent(operations.eventName));
        }
      },
      undo: () => {
        // Eliminar la entidad
        if (createdEntity) {
          const entityId = entityConfig.getId!(createdEntity);
          operations.remove(entityId);
        }
      },
    };

    // Ejecutar comando (se registra automáticamente en el historial de undo/redo)
    undoRedoService.execute(createCommand);

    // Limpiar preview
    setElementPreview(null);
  };

  /**
   * Cancela la creación del elemento desde el preview
   */
  const handleCancelCreate = ():void => {
    console.log('[MapLayout] Creación de elemento cancelada');
    setElementPreview(null);
  };

  /**
   * Finaliza el dibujo de una ruta
   * Actualiza la geometría en BD y mantiene el panel abierto para completar datos
   */
  const finishRouteDrawing = (): void => {
    if (!isDrawingRoute) return;

    // Validar mínimo 2 puntos
    if (routePoints.length < 2) {
      console.warn('[MapLayout] Se requieren al menos 2 puntos para crear una ruta');
      alert('Se requieren al menos 2 puntos para crear una ruta');
      return;
    }

    if (!createRoutePanelData) {
      console.error('[MapLayout] No hay datos de panel para actualizar');
      return;
    }

    console.log('[MapLayout] Finalizando dibujo de ruta con', routePoints.length, 'puntos');

    // Crear geometría final
    const finalGeometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates: routePoints,
    };

    // Actualizar geometría en BD
    editRoute(createRoutePanelData.routeId, {
      geometry: finalGeometry,
    })
      .then((updatedRoute) => {
        if (!updatedRoute) {
          console.error('[MapLayout] Error al actualizar geometría de ruta');
          return;
        }

        console.log('[MapLayout] Geometría de ruta actualizada en BD');

        // Actualizar datos del panel con la geometría final
        setCreateRoutePanelData({
          ...createRoutePanelData,
          geometry: finalGeometry,
        });

        // Limpiar estado de dibujo (pero mantener createRoutePanelData para el panel)
        setIsDrawingRoute(false);
        setRoutePoints([]);
        setShowRouteDrawingBanner(false); // Cerrar banner
        setActiveTool(null); // Desactivar herramienta

        console.log('[MapLayout] Ruta finalizada. Panel permanece abierto para completar datos.');

        // Dispatch evento para actualizar lista de rutas
        window.dispatchEvent(new CustomEvent('route-updated'));
      })
      .catch((err) => {
        console.error('[MapLayout] Error al actualizar ruta:', err);
        alert('Error al actualizar ruta. Los cambios no se guardaron.');
      });
  };

  /**
   * Cancela el dibujo de una ruta
   * Limpia todo: estado, panel, banner
   * Funciona tanto durante el dibujo como después de presionar Enter
   */
  const cancelRouteDrawing = (): void => {
    // Permitir cancelar en cualquier momento (durante dibujo o con panel abierto)
    if (!isDrawingRoute && !createRoutePanelData) return;

    console.log('[MapLayout] Cancelando dibujo de ruta');

    // Limpiar todo el estado
    setIsDrawingRoute(false);
    setRoutePoints([]);
    setShowRouteDrawingBanner(false);
    setCreateRoutePanelData(null); // Cerrar panel y eliminar capas del mapa
    setActiveTool(null);
    closeRightPanel();

    console.log('[MapLayout] Dibujo de ruta cancelado completamente');
  };

  /**
   * Maneja eventos de teclado durante el dibujo de ruta
   * Enter: Finalizar
   * Escape: Cancelar
   */
  useEffect(() => {
    if (!isDrawingRoute) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        finishRouteDrawing();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelRouteDrawing();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawingRoute, routePoints]); // Dependencias necesarias

  /**
   * Atajo de teclado: Enter para cerrar panel sin desactivar herramienta
   *
   * @remarks
   * Cuando la herramienta de árboles está activa y hay un árbol seleccionado (panel abierto),
   * presionar Enter cierra el panel y deselecciona el árbol, pero MANTIENE la herramienta activa.
   * Esto permite flujo de creación rápida masiva: clic → Enter → clic → Enter → indefinidamente.
   *
   * Solo funciona si el foco NO está en un input/textarea/select (para no interferir con la escritura).
   */
  useEffect(() => {
    // Solo activar cuando herramienta de árboles está activa y hay árbol seleccionado
    if (activeTool !== 'create-tree' || !selectedTreeId) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') {
        // Verificar que el foco no esté en un campo de texto
        const activeElement = document.activeElement;
        const isTyping = activeElement instanceof HTMLInputElement ||
                         activeElement instanceof HTMLTextAreaElement ||
                         activeElement instanceof HTMLSelectElement;

        if (isTyping) {
          // Usuario está escribiendo en un input, no interferir
          return;
        }

        // Cerrar panel y deseleccionar árbol sin desactivar herramienta
        e.preventDefault();
        selectTree(null);
        closeRightPanel();
        // activeTool sigue siendo 'create-tree' - crosshair permanece activo
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool, selectedTreeId, selectTree, closeRightPanel]);

  /**
   * Actualizar ref de elementPreview
   */
  useEffect(() => {
    elementPreviewRef.current = elementPreview;
  }, [elementPreview]);

  /**
   * Atajo de teclado: Q para desactivar herramienta sin popover abierto
   *
   * @remarks
   * Comportamiento diferenciado de Q:
   * - Si hay popover abierto (elementPreview !== null): ElementPreview maneja el Q, cancela el popover pero mantiene herramienta activa
   * - Si NO hay popover (elementPreview === null): Q desactiva completamente la herramienta de creación
   *
   * Esto evita confusión al usuario:
   * - Q con popover = "No quiero este punto específico, busco otro lugar"
   * - Q sin popover = "Ya terminé de crear árboles, salir del modo creación" (Quit)
   *
   * @important
   * Se usa 'Q' en lugar de ESC porque algunos navegadores/OS (especialmente Mac) capturan
   * ESC a nivel del sistema y no permiten interceptarlo desde JavaScript.
   *
   * Usa elementPreviewRef.current en lugar de elementPreview directamente para evitar
   * que el handler se reregistre cada vez que cambia el preview (causaría closure stale)
   */
  useEffect(() => {
    // Solo activar cuando herramienta de árboles está activa
    if (activeTool !== 'create-tree') return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      // Detectar Q (minúscula o mayúscula) para salir del modo creación
      if (e.key === 'q' || e.key === 'Q') {
        // Solo procesar en keydown, no en keyup (para evitar duplicados)
        if (e.type !== 'keydown') return;

        // Verificar que no esté escribiendo en un input
        const activeElement = document.activeElement;
        const isTyping = activeElement instanceof HTMLInputElement ||
                         activeElement instanceof HTMLTextAreaElement ||
                         activeElement instanceof HTMLSelectElement;

        if (isTyping) return;

        // Usar ref para obtener valor actual (no el capturado en closure)
        const currentPreview = elementPreviewRef.current;

        // Si hay popover abierto, ElementPreview lo maneja
        if (currentPreview !== null) return;

        // Si NO hay popover, desactivar herramienta completamente
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        void handleToolChange(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [activeTool, handleToolChange]);

  /**
   * Escape en modo población: limpia la selección del segmento activo.
   * Permite cerrar el EdgeDetailsPanel sin usar el mouse.
   */
  useEffect(() => {
    if (!populationModeActive || !selectedEdgeId) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      const active = document.activeElement;
      const isTyping =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement;
      if (isTyping) return;

      e.preventDefault();
      setSelectedEdgeId(null);
      setUrbanNetworkSelectedEdgeId(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [populationModeActive, selectedEdgeId, setSelectedEdgeId, setUrbanNetworkSelectedEdgeId]);

  // NO capturamos posición del mouse - solo dibujamos líneas entre nodos confirmados

  /**
   * Actualiza la geometría del CreateRoutePanel y BD en tiempo real mientras se dibuja
   */
  useEffect(() => {
    if (!isDrawingRoute || routePoints.length === 0 || !createRoutePanelData) return;

    // Actualizar geometría en el panel cada vez que se agregan puntos
    const updatedGeometry: GeoJSON.LineString = {
      type: 'LineString',
      coordinates: routePoints.length === 1
        ? [routePoints[0], routePoints[0]] // Duplicar si solo hay 1 punto
        : routePoints, // Usar todos los puntos si hay 2+
    };

    setCreateRoutePanelData({
      ...createRoutePanelData,
      geometry: updatedGeometry,
    });

    // Auto-guardar geometría en BD si hay al menos 2 puntos
    if (routePoints.length >= 2 && createRoutePanelData.routeId) {
      editRoute(createRoutePanelData.routeId, {
        geometry: updatedGeometry,
      })
        .then(() => {
          console.log('[MapLayout] Geometría auto-guardada en BD');
        })
        .catch((err) => {
          console.error('[MapLayout] Error al auto-guardar geometría:', err);
        });
    }
  }, [isDrawingRoute, routePoints, createRoutePanelData?.routeId, editRoute]); // Solo depender del routeId, no del objeto completo

  /**
   * Crea/elimina las capas de dibujo cuando cambia isDrawingRoute o createRoutePanelData
   * - Mientras se dibuja (isDrawingRoute=true): muestra línea + nodos circulares
   * - Después de Enter (isDrawingRoute=false pero createRoutePanelData existe): solo muestra línea
   * - Al cerrar/guardar panel (createRoutePanelData=null): elimina todas las capas
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const sourceId = 'temp-route-source';
    const layerId = 'temp-route-layer';
    const pointsLayerId = 'temp-route-points-layer';

    // Mostrar capas si estamos dibujando O si el panel está abierto
    const shouldShowLayers = isDrawingRoute || createRoutePanelData !== null;

    if (shouldShowLayers) {
      // CREAR layers si no existen
      if (!map.getSource(sourceId)) {
        // GeoJSON vacío inicial
        const emptyGeojson: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [],
        };

        map.addSource(sourceId, {
          type: 'geojson',
          data: emptyGeojson,
        });

        // Agregar capa de línea
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          filter: ['==', ['geometry-type'], 'LineString'],
          paint: {
            'line-color': '#2FA4B5', // WESTEER cyan
            'line-width': 4,
            // Línea sólida (no dashed) ya que solo mostramos nodos confirmados
          },
        });

        console.log('[MapLayout] Capa de línea creada');
      }

      // Capa de puntos: solo visible mientras dibujamos
      if (isDrawingRoute && !map.getLayer(pointsLayerId)) {
        map.addLayer({
          id: pointsLayerId,
          type: 'circle',
          source: sourceId,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 6,
            'circle-color': '#2FA4B5',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 2,
          },
        });
        console.log('[MapLayout] Capa de puntos creada');
      } else if (!isDrawingRoute && map.getLayer(pointsLayerId)) {
        // Eliminar capa de puntos al terminar de dibujar (Enter presionado)
        map.removeLayer(pointsLayerId);
        console.log('[MapLayout] Capa de puntos eliminada - solo línea visible');
      }
    } else {
      // ELIMINAR todas las layers cuando se cierra el panel
      if (map.getLayer(pointsLayerId)) {
        map.removeLayer(pointsLayerId);
      }
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
      console.log('[MapLayout] Capas de dibujo temporal eliminadas');
    }

    // NO incluir cleanup aquí - causaría parpadeo en cada cambio de estado
  }, [isDrawingRoute, createRoutePanelData]); // Depende de ambos estados

  /**
   * Cleanup separado: solo se ejecuta al desmontar el componente
   * Esto evita que las capas se eliminen/recreen en cada cambio de estado
   */
  useEffect(() => {
    return () => {
      const map = mapEngine.getMap();
      if (!map || !isMapReadyForSources(map)) return;

      const sourceId = 'temp-route-source';
      const layerId = 'temp-route-layer';
      const pointsLayerId = 'temp-route-points-layer';

      if (map.getLayer(pointsLayerId)) map.removeLayer(pointsLayerId);
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, []); // Dependencias vacías = solo ejecuta al desmontar

  /**
   * Actualiza los datos de la línea en cada click (solo puntos confirmados)
   * NO incluye línea temporal al cursor - solo líneas entre nodos fijos
   * Después de Enter, solo muestra la línea (sin puntos)
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const sourceId = 'temp-route-source';
    const source = map.getSource(sourceId) as any;

    if (!source) return; // Las capas aún no se crearon

    // Determinar qué coordenadas usar
    let coordinates: [number, number][] = [];

    if (isDrawingRoute && routePoints.length > 0) {
      // Mientras dibujamos: usar routePoints
      coordinates = routePoints;
    } else if (!isDrawingRoute && createRoutePanelData?.geometry) {
      // Después de Enter: usar la geometría del panel
      coordinates = createRoutePanelData.geometry.coordinates as [number, number][];
    }

    if (coordinates.length === 0) {
      // Sin datos, limpiar el source
      source.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const features: GeoJSON.Feature[] = [];

    // Solo agregar puntos si estamos dibujando (isDrawingRoute=true)
    if (isDrawingRoute) {
      const pointFeatures: GeoJSON.Feature<GeoJSON.Point>[] = coordinates.map((coord, idx) => ({
        type: 'Feature',
        properties: { index: idx + 1 },
        geometry: {
          type: 'Point',
          coordinates: coord,
        },
      }));
      features.push(...pointFeatures);
    }

    // Agregar línea si hay al menos 2 puntos
    if (coordinates.length >= 2) {
      const lineFeature: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates,
        },
      };
      features.unshift(lineFeature); // Línea primero para que se dibuje debajo de los puntos
    }

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    // SOLO actualizar los datos del source (no recrea layers)
    source.setData(geojson);
  }, [isDrawingRoute, routePoints, createRoutePanelData]); // Depende de todos los estados relevantes

  /**
   * Escucha evento 'waste-open-create-route-panel' para abrir panel de creación
   * Este evento se usa para rutas creadas desde "Conectar contenedores" u otros métodos
   */
  useEffect(() => {
    const handleOpenCreateRoutePanel = (e: Event): void => {
      const customEvent = e as CustomEvent;
      const { geometry, containerIds } = customEvent.detail;

      console.log('[MapLayout] Evento recibido para crear ruta con geometría:', geometry);

      if (!projectId) {
        console.error('[MapLayout] No hay proyecto activo, no se puede crear ruta');
        alert('No hay proyecto activo');
        return;
      }

      // Auto-generar código de ruta
      const routeCode = `RT-${String(routes.length + 1).padStart(3, '0')}`;

      // Crear ruta en BD con la geometría provista
      createRoute({
        code: routeCode,
        name: `Nueva ruta ${routes.length + 1}`,
        geometry,
        containerIds: containerIds || [],
        frequency: 'daily',
        estimatedDuration: 0,
        estimatedDistance: 0,
        operatingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        startTime: '08:00',
        status: 'active',
      })
        .then((createdRoute) => {
          if (!createdRoute) {
            console.error('[MapLayout] Error al crear ruta en BD');
            alert('Error al crear ruta. Por favor intenta nuevamente.');
            return;
          }

          console.log('[MapLayout] Ruta creada en BD:', createdRoute.id);

          // Guardar datos para el panel con el ID de la ruta creada
          setCreateRoutePanelData({
            routeId: createdRoute.id,
            geometry,
            containerIds: containerIds || [],
          });

          console.log('[MapLayout] CreateRoutePanel abierto para ruta:', createdRoute.id);

          // Dispatch evento para actualizar lista de rutas
          window.dispatchEvent(new CustomEvent('route-created'));
        })
        .catch((err) => {
          console.error('[MapLayout] Error al crear ruta:', err);
          alert('Error al crear ruta. Por favor intenta nuevamente.');
        });
    };

    window.addEventListener('waste-open-create-route-panel', handleOpenCreateRoutePanel);
    return () => window.removeEventListener('waste-open-create-route-panel', handleOpenCreateRoutePanel);
  }, [projectId, routes, createRoute]);

  /**
   * Escucha evento 'network-nodes-select' para abrir panel de detalles de nodo
   */
  useEffect(() => {
    const handleNodeSelect = (e: Event): void => {
      const customEvent = e as CustomEvent;
      const { elementId } = customEvent.detail;

      console.log('[MapLayout] Nodo seleccionado:', elementId);
      setSelectedNodeId(elementId);
      setSelectedEdgeId(null); // Limpiar selección de edge
    };

    window.addEventListener('network-nodes-select', handleNodeSelect);
    return () => window.removeEventListener('network-nodes-select', handleNodeSelect);
  }, []);

  /**
   * Escucha evento 'network-edges-select' para abrir panel de detalles de edge
   */
  useEffect(() => {
    const handleEdgeSelect = (e: Event): void => {
      const customEvent = e as CustomEvent;
      const { elementId } = customEvent.detail;

      console.log('[MapLayout] Edge seleccionado:', elementId);
      hierarchySegmentSelectionRef.current = false;
      setSelectedEdgeId(elementId);
      setSelectedNodeId(null); // Limpiar selección de nodo
    };

    window.addEventListener('network-edges-select', handleEdgeSelect);
    return () => window.removeEventListener('network-edges-select', handleEdgeSelect);
  }, []);

  useEffect(() => {
    const handleEdgeHover = (e: Event): void => {
      const customEvent = e as CustomEvent<NetworkEdgeHoverDetail>;
      const detail = customEvent.detail;

      if (!detail?.elementId) {
        setSemanticHover(null);
        return;
      }

      setSemanticHover((previous) => {
        if (
          previous &&
          previous.elementId === detail.elementId &&
          Math.abs(previous.clientX - detail.clientX) < 12 &&
          Math.abs(previous.clientY - detail.clientY) < 12
        ) {
          return previous;
        }

        return detail;
      });
    };

    window.addEventListener('network-edges-hover', handleEdgeHover);
    return () => window.removeEventListener('network-edges-hover', handleEdgeHover);
  }, []);

  const hoveredSemantic = useMemo(() => {
    if (!projectId || !semanticHover?.elementId) {
      return undefined;
    }

    return canonicalNetworkApi.peekCachedEdgeSemanticName(projectId, semanticHover.elementId);
  }, [projectId, semanticHover]);

  const hoverTooltipStyle = useMemo(() => {
    if (!semanticHover) {
      return undefined;
    }

    return {
      left: Math.min(semanticHover.clientX + 14, window.innerWidth - 320),
      top: Math.max(semanticHover.clientY - 18, 72),
    };
  }, [semanticHover]);

  return (
    <div
      className="map-layout"
      data-left-panel={leftPanelVisible ? 'visible' : 'collapsed'}
      data-right-panel={rightPanelVisible ? 'visible' : 'hidden'}
      data-network-loading-initial={isLoadingScreenActive ? 'true' : 'false'}
    >
      {semanticHover?.elementId && (
        <div
          className="network-edge-hover-tooltip"
          style={hoverTooltipStyle}
          aria-hidden="true"
        >
          <div className="network-edge-hover-tooltip__title">
            {getEdgeSemanticDisplayName(hoveredSemantic)}
          </div>
          <div
            className={`network-edge-hover-tooltip__status network-edge-hover-tooltip__status--${getEdgeNamingStatusTone(hoveredSemantic?.namingStatus)}`}
          >
            {hoveredSemantic
              ? getEdgeNamingStatusLabel(hoveredSemantic.namingStatus)
              : 'Semántica no cargada'}
          </div>
        </div>
      )}

      {/* TopBar fixed en la parte superior */}
      <TopBar visibleModes={visibleModes} />

      {/* NetworkLoadingScreen: full-screen hasta que el style está listo.
          Fase 2D.2: fade-out en cuanto mapStyleReady (primer styledata:style).
          Unmount en initialNetworkLoadComplete para no perder el indicador de red. */}
      {!initialNetworkLoadComplete && (
        <NetworkLoadingScreen isFadingOut={mapStyleReady} />
      )}

      {/* NetworkLoadingPill: indicador no bloqueante que cubre la brecha entre
          el fade-out del NLS (mapStyleReady ~756ms) y la aparición del preview SPIKE.
          Al llegar previewNetworkActive, añade .nlp--exit → 1s de gracia + fade-out suave.
          Se desmonta cuando mapStyleReady vuelve a false (cambio de proyecto).
          pointer-events:none — no bloquea paneles ni interacción. */}
      {mapStyleReady && (
        <div className={`nlp${previewNetworkActive ? ' nlp--exit' : ''}`} aria-live="polite" aria-label="Red urbana cargando">
          <span className="nlp__spinner" aria-hidden="true" />
          <span className="nlp__text">
            {previewNetworkBooting ? 'Cargando vista previa de red\u2026' : 'Preparando red urbana\u2026'}
          </span>
        </div>
      )}

      {/* Paneles laterales - CORE vacíos, listos para inyectar contenido */}
      {showPanels && (
        <>
          {/* Panel izquierdo: visible por defecto, colapsable */}
          <LeftPanel
            initialCollapsed={!leftPanelVisible}
            onToggle={(collapsed) => {
              if (collapsed !== !leftPanelVisible) {
                toggleLeftPanel();
              }
            }}
          >
            {/* Zona superior: contenido dinámico del módulo activo (crece para llenar espacio) */}
            <div className="left-panel-module-section">
              {/* Renderizar panel según módulo y modo operacional */}
              {operationalMode === 'analyze' && populationModeActive ? (
                /* Modo Analizar + indicador población → panel de contexto de población */
                <PopulationAnalysisPanel />
              ) : operationalMode === 'analyze' ? (
                /* Modo Analizar genérico (sin indicador de población activo) */
                <AnalyzePanel />
              ) : activeModuleDefinition?.leftPanel ? (
                /* Renderizar panel del módulo registrado dinámicamente */
                (() => {
                  const LeftPanelComponent = activeModuleDefinition.leftPanel;
                  const Provider = activeModuleDefinition.provider;

                  // Si el módulo tiene provider, envolver el panel con él
                  if (Provider && projectId) {
                    return (
                      <Provider projectId={projectId}>
                        <LeftPanelComponent />
                      </Provider>
                    );
                  }

                  // Sin provider, renderizar panel directamente
                  return <LeftPanelComponent />;
                })()
              ) : null /* Sin módulo activo: no renderizar contenido */}
            </div>

            {/* Zona inferior: Panel de Mallas H3 — siempre visible */}
            <NetworksAndGridsPanel />
          </LeftPanel>

          {/* Panel derecho: oculto por defecto, aparece con selección */}
          <RightPanel
            feature={
              operationalMode === 'plan' ? { type: 'plan' } :
              (operationalMode === 'analyze' && zonalSelection) ? zonalSelection :
            editingGrid ? editingGrid :
            (activePanel === 'grid-cells' && activeGrid) ? activeGrid :
            activePanel === 'base-roads' ? { type: 'base-roads' } :
            activePanel === 'network-style' ? { type: 'network-style' } :
            selectedNodeId ? { type: 'network-node', id: selectedNodeId } :
            selectedEdgeId ? { type: 'network-edge', id: selectedEdgeId } :
            activePanel === 'network-analysis' ? { type: 'network-analysis' } :
            selectedTree ? selectedTree :
            selectedContainerId ? containers.find((c) => c.id === selectedContainerId) :
            createRoutePanelData ? { type: 'create-route' } :
            selectedBuildingForReview ? { type: 'building', ...selectedBuildingForReview } :
            selectedCensusRadio ? { type: 'census-radio', radioCode: selectedCensusRadio.radioCode } :
            selectedUrbanEntity ? { type: 'urban-entity', id: selectedUrbanEntity.id } :
            selectedFeature?.type === 'network-analysis' ? null : selectedFeature
          }
            onClose={async () => {
              // Verificar cambios antes de cerrar
              if (operationalMode === 'plan') {
                setOperationalMode('explore');
              }
              if (operationalMode === 'analyze' && zonalSelection) {
                setZonalSelection(null);
                // Emitir evento para limpiar selección en el mapa
                window.dispatchEvent(new CustomEvent('zonal-selection-cleared'));
              }
              if (editingGrid) {
                setEditingGrid(null);
              }
              if (activeGrid) {
                // Desactivar la malla activa
                setActiveGridId(null);
                setCellRows([]);
                setHoveredCellId(null);
                setSelectedCellId(null);
              }
              if (activePanel === 'base-roads') {
                // Limpiar selección de vías
                handleRoadHover(null);
                handleRoadSelect(null);
              }
              if (selectedNodeId) {
                setSelectedNodeId(null);
                if (activePanel === 'network-analysis') {
                  setActivePanel(null);
                }
              }
              if (selectedEdgeId) {
                setSelectedEdgeId(null);
                setUrbanNetworkSelectedEdgeId(null);
                const restoredOverview = await restoreNetworkAnalysisOverview();
                if (restoredOverview) {
                  return;
                }
              }
              if (!selectedNodeId && !selectedEdgeId && activePanel === 'network-analysis') {
                closeNetworkAnalysisOverview();
                return;
              }
              if (selectedTreeId) {
                selectTree(null);
              }
              if (selectedWasteContainer) {
                setSelectedWasteContainer(null);
              }
              if (selectedContainerId) {
                selectContainer(null);
              }
              if (selectedBuildingForReview) {
                closeBuildingReview();
              }
              if (selectedCensusRadio) {
                clearCensusRadio();
              }
              closeRightPanel();
            }}
          >
            {/* SOLO UNO DE LOS PANELES A LA VEZ */}
            {operationalMode === 'plan' ? (
              /* Panel de planificación de escenarios */
              <ScenariosPanel />
            ) : (operationalMode === 'analyze' && zonalSelection) ? (
              /* Panel de estadísticas zonales */
              <ZonalStatsPanel
                selection={zonalSelection}
                indicators={calculatedIndicators}
                onClose={() => {
                  setZonalSelection(null);
                  window.dispatchEvent(new CustomEvent('zonal-selection-cleared'));
                  closeRightPanel();
                }}
              />
            ) : editingGrid ? (
              /* Panel de edición de malla */
              <GridEditPanel
                grid={editingGrid}
                onClose={() => {
                  setEditingGrid(null);
                  closeRightPanel();
                }}
                onSaved={(updated) => {
                  setEditingGrid(updated);
                }}
              />
            ) : (activePanel === 'grid-cells' && activeGrid && detailedCell) ? (
              /* Panel de detalles de celda específica */
              <CellDetailsPanel
                grid={activeGrid}
                cell={detailedCell}
                operationalMode={operationalMode === 'inventory' ? 'edit' : 'analyze'}
                onBack={() => {
                  setDetailedCellId(null);
                }}
                onClose={() => {
                  setDetailedCellId(null);
                }}
                onSaveCellName={handleCellNameChange}
              />
            ) : (activePanel === 'grid-cells' && activeGrid) ? (
              /* Panel de celdas de malla - Solo si activePanel === 'grid-cells' */
              <GridCellsPanel
                activeGrid={activeGrid}
                cellRows={cellRows}
                hoveredCellId={hoveredCellId}
                onCellHover={handleCellHover}
                selectedCellId={selectedCellId}
                onCellSelect={handleCellSelect}
                onZoomToCell={handleZoomToCell}
                onCellNameChange={handleCellNameChange}
                onCellStatsClick={handleCellStatsClick}
                onClose={() => {
                  setActiveGridId(null);
                  setCellRows([]);
                  setHoveredCellId(null);
                  setSelectedCellId(null);
                  setDetailedCellId(null);
                  closeRightPanel();
                }}
              />
            ) : activePanel === 'base-roads' ? (
              /* Panel de vías base del IGN */
              <RoadsListPanel
                onRoadHover={handleRoadHover}
                onRoadSelect={handleRoadSelect}
                onClose={() => {
                  closeRightPanel();
                }}
              />
            ) : activePanel === 'network-style' ? (
              /* Panel de edición de estilos de red urbana */
              <NetworkStylePanel />
            ) : selectedNodeId ? (
              /* Panel de detalles de nodo */
              <NodeDetailsPanel
                nodeId={selectedNodeId}
                onClose={() => {
                  setSelectedNodeId(null);
                  closeRightPanel();
                }}
              />
            ) : selectedEdgeId ? (
              /* Panel de detalles de edge */
              <EdgeDetailsPanel
                edgeId={selectedEdgeId}
                projectId={projectId}
                hierarchyData={selectedEdgeHierarchyData}
                hierarchyProfile={hierarchyAnalysis.profile}
                initialTab={edgeDetailsInitialTab}
                onClose={async () => {
                  setSelectedEdgeId(null);
                  setUrbanNetworkSelectedEdgeId(null); // Limpiar highlight en mapa
                  const restoredOverview = await restoreNetworkAnalysisOverview();
                  if (restoredOverview) {
                    return;
                  }
                  closeRightPanel();
                }}
              />
            ) : activePanel === 'network-analysis' ? (
              <NetworkAnalysisOverviewPanel
                hierarchyData={hierarchyAnalysis.data}
                hierarchyLoading={hierarchyAnalysis.loading}
                hierarchyError={hierarchyAnalysis.error}
                hierarchyProfile={hierarchyAnalysis.profile}
                onHierarchyProfileChange={hierarchyAnalysis.setProfile}
                onClose={closeNetworkAnalysisOverview}
              />
            ) : selectedTree ? (
              /* Panel de detalles de árbol */
              <TreeDetailsPanel
                tree={selectedTree}
                projectId={projectId}
                readOnly={operationalMode !== 'inventory'}
                onSave={async (updatedTree) => {
                  try {
                    // Verificar que hay un proyecto activo
                    if (!projectId) {
                      console.error('[MapLayout] No hay proyecto activo. Configure VITE_DEFAULT_PROJECT_ID en .env');
                      alert('Error: No hay proyecto activo configurado');
                      return;
                    }

                    // Guardar en base de datos
                    // saveTree() actualiza el estado local internamente (trees + selectedTreeId)
                    const savedTree = await saveTree(updatedTree, projectId);

                    console.log('[MapLayout] Árbol guardado exitosamente en BD:', savedTree.id);

                    // Si el árbol estaba en "recién creados", removerlo del tracking genérico
                    // porque el usuario ya lo caracterizó y guardó explícitamente
                    if (isEntityTracked('tree', savedTree.id)) {
                      untrackEntity('tree', savedTree.id);
                      console.log('[MapLayout] Árbol removido del tracking de "recién creados"');
                    }

                    // Disparar evento para que TreesPanel actualice la lista
                    window.dispatchEvent(new CustomEvent('tree-updated'));
                  } catch (error) {
                    console.error('[MapLayout] Error al guardar árbol:', error);
                    alert(`Error al guardar árbol: ${error instanceof Error ? error.message : 'Error desconocido'}`);
                  }
                }}
                onCancel={async () => {
                  if (!selectedTreeId) {
                    selectTree(null);
                    closeRightPanel();
                    return;
                  }

                  const tree = trees.find((t) => t.id === selectedTreeId);
                  if (!tree) {
                    selectTree(null);
                    closeRightPanel();
                    return;
                  }

                  // Verificar si es un árbol recién creado (guardado automáticamente pero usuario no lo caracterizó)
                  const isNewlyCreated = isEntityTracked('tree', tree.id);

                  if (isNewlyCreated) {
                    console.log('[MapLayout] Árbol recién creado cancelado. Eliminando de BD y del mapa:', tree.id);

                    // Eliminar del mapa primero
                    removeTree(tree.id);

                    // Eliminar del tracking genérico
                    untrackEntity('tree', tree.id);

                    // Eliminar de BD usando el entity registry
                    const entityConfig = getEntityConfig('tree');
                    if (entityConfig) {
                      try {
                        await entityConfig.apiService.delete(tree.id);
                        console.log('[MapLayout] Árbol eliminado exitosamente de BD');
                      } catch (error) {
                        console.error('[MapLayout] Error al eliminar árbol de BD:', error);
                        // Aunque falle la eliminación en BD, el árbol ya se removió del mapa
                      }
                    }
                  } else if (tree.id.startsWith('tree-')) {
                    // Árbol con ID temporal (guardado falló o no hay proyecto)
                    removeTree(tree.id);
                    console.log('[MapLayout] Árbol con ID temporal descartado sin guardar:', tree.id);
                  } else {
                    // Árbol existente cargado de BD al inicio: solo cerrar panel
                    console.log('[MapLayout] Cancelar edición de árbol existente (se mantiene con datos originales)');
                  }

                  selectTree(null);
                  closeRightPanel();
                }}
                onDelete={(treeId) => {
                  removeTree(treeId);
                  selectTree(null);
                  closeRightPanel();
                  console.log('[MapLayout] Árbol eliminado:', treeId);
                }}
              />
            ) : selectedFountain ? (
              /* Panel de detalles de fuente */
              <FountainDetailsPanel
                fountain={selectedFountain}
                readOnly={operationalMode !== 'inventory'}
                onSave={async (updatedFountain) => {
                  try {
                    // Verificar que hay un proyecto activo
                    if (!projectId) {
                      console.error('[MapLayout] No hay proyecto activo. Configure VITE_DEFAULT_PROJECT_ID en .env');
                      alert('Error: No hay proyecto activo configurado');
                      return;
                    }

                    // Guardar en base de datos
                    const savedFountain = await saveFountain(updatedFountain, projectId);

                    console.log('[MapLayout] Fuente guardada exitosamente en BD:', savedFountain.id);

                    // Si la fuente estaba en "recién creadas", removerla del tracking genérico
                    if (isEntityTracked('fountain', savedFountain.id)) {
                      untrackEntity('fountain', savedFountain.id);
                      console.log('[MapLayout] Fuente removida del tracking de "recién creadas"');
                    }

                    // Disparar evento para que FountainsPanel actualice la lista
                    window.dispatchEvent(new CustomEvent('fountain-updated'));
                  } catch (error) {
                    console.error('[MapLayout] Error al guardar fuente:', error);
                    alert(`Error al guardar fuente: ${error instanceof Error ? error.message : 'Error desconocido'}`);
                  }
                }}
                onCancel={async () => {
                  if (!selectedFountainId) {
                    selectFountain(null);
                    closeRightPanel();
                    return;
                  }

                  const fountain = fountains.find((f) => f.id === selectedFountainId);
                  if (!fountain) {
                    selectFountain(null);
                    closeRightPanel();
                    return;
                  }

                  // Verificar si es una fuente recién creada
                  const isNewlyCreated = isEntityTracked('fountain', fountain.id);

                  if (isNewlyCreated) {
                    console.log('[MapLayout] Fuente recién creada cancelada. Eliminando de BD y del mapa:', fountain.id);

                    // Eliminar del mapa primero
                    removeFountain(fountain.id);

                    // Eliminar del tracking genérico
                    untrackEntity('fountain', fountain.id);

                    // Eliminar de BD usando el entity registry
                    const entityConfig = getEntityConfig('fountain');
                    if (entityConfig) {
                      try {
                        await entityConfig.apiService.delete(fountain.id);
                        console.log('[MapLayout] Fuente eliminada exitosamente de BD');
                      } catch (error) {
                        console.error('[MapLayout] Error al eliminar fuente de BD:', error);
                      }
                    }
                  } else if (fountain.id.startsWith('fountain-')) {
                    // Fuente con ID temporal
                    removeFountain(fountain.id);
                    console.log('[MapLayout] Fuente con ID temporal descartada sin guardar:', fountain.id);
                  } else {
                    // Fuente existente: solo cerrar panel
                    console.log('[MapLayout] Cancelar edición de fuente existente');
                  }

                  selectFountain(null);
                  closeRightPanel();
                }}
                onDelete={(fountainId) => {
                  removeFountain(fountainId);
                  selectFountain(null);
                  closeRightPanel();
                  console.log('[MapLayout] Fuente eliminada:', fountainId);
                }}
              />
            ) : selectedWasteContainer ? (
              /* Panel de detalles de contenedor Waste persistido */
              <WasteContainerDetailsPanel
                container={selectedWasteContainer}
                readOnly={operationalMode !== 'inventory'}
                onSave={(updatedContainer) => {
                  setSelectedWasteContainer(updatedContainer);
                  console.log('[MapLayout] Contenedor Waste actualizado:', updatedContainer.id);
                }}
                onCancel={() => {
                  setSelectedWasteContainer(null);
                  closeRightPanel();
                }}
                onDelete={() => {
                  setSelectedWasteContainer(null);
                  closeRightPanel();
                }}
              />
            ) : selectedWasteDepot ? (
              <WasteDepotDetailsPanel
                depot={selectedWasteDepot}
                onClose={() => {
                  setSelectedWasteDepot(null);
                  closeRightPanel();
                }}
              />
            ) : selectedWasteHotspot ? (
              <WasteHotspotDetailsPanel
                hotspot={selectedWasteHotspot}
                onClose={() => {
                  setSelectedWasteHotspot(null);
                  closeRightPanel();
                }}
              />
            ) : selectedTransitStop ? (
              <TransitStopDetailsPanel
                stop={selectedTransitStop}
                onClose={() => {
                  setSelectedTransitStop(null);
                  closeRightPanel();
                }}
              />
            ) : selectedContainerId && containers.find((c) => c.id === selectedContainerId) ? (
              /* Panel de detalles de contenedor local */
              <LocalContainerDetailsPanel
                container={containers.find((c) => c.id === selectedContainerId)!}
                readOnly={operationalMode !== 'inventory'}
                onSave={(updatedContainer) => {
                  updateContainer(updatedContainer);

                  // Si es una entidad recién creada, remover del tracking
                  if (isEntityTracked('container', updatedContainer.id)) {
                    untrackEntity('container', updatedContainer.id);
                  }

                  console.log('[MapLayout] Contenedor actualizado:', updatedContainer.id);
                }}
                onCancel={async () => {
                  const container = containers.find((c) => c.id === selectedContainerId);
                  if (!container) return;

                  // Verificar si es una entidad recién creada usando tracking genérico
                  const isNewlyCreated = isEntityTracked('container', container.id);

                  if (isNewlyCreated) {
                    // Eliminar del mapa
                    removeContainer(container.id);
                    untrackEntity('container', container.id);

                    // Eliminar de BD usando entity registry
                    const entityConfig = getEntityConfig('container');
                    if (entityConfig) {
                      try {
                        await entityConfig.apiService.delete(container.id);
                        console.log('[MapLayout] Contenedor nuevo eliminado de BD:', container.id);
                      } catch (error) {
                        console.error('[MapLayout] Error al eliminar contenedor de BD:', error);
                      }
                    }

                    console.log('[MapLayout] Contenedor nuevo descartado:', container.id);
                  }

                  selectContainer(null);
                  closeRightPanel();
                }}
                onDelete={(containerId) => {
                  removeContainer(containerId);
                  selectContainer(null);
                  closeRightPanel();
                  console.log('[MapLayout] Contenedor eliminado:', containerId);
                }}
              />
            ) : createRoutePanelData ? (
              /* Panel de creación/edición de ruta (módulo waste) */
              <CreateRoutePanel
                routeId={createRoutePanelData.routeId}
                geometry={createRoutePanelData.geometry}
                containerIds={createRoutePanelData.containerIds}
                isDrawing={isDrawingRoute} // Indicar si estamos en modo dibujo
                onClose={() => {
                  // Si estamos dibujando, cancelar el dibujo completo
                  if (isDrawingRoute) {
                    cancelRouteDrawing();
                  } else {
                    // Si ya finalizamos el dibujo, solo cerrar el panel
                    setCreateRoutePanelData(null);
                    closeRightPanel();
                  }
                  console.log('[MapLayout] CreateRoutePanel cerrado');
                }}
                onCreated={() => {
                  // Ruta creada/actualizada exitosamente
                  setCreateRoutePanelData(null);
                  setShowRouteDrawingBanner(false);
                  closeRightPanel();
                  console.log('[MapLayout] Ruta guardada exitosamente desde CreateRoutePanel');
                }}
              />
            ) : selectedBuildingForReview ? (
              /* Panel de revisión de edificio (V1.4 — panel lateral derecho) */
              <BuildingDetailsSidebar
                buildingId={selectedBuildingForReview.buildingId}
                projectId={projectId ?? ''}
                initialProperties={selectedBuildingForReview.properties}
                onClose={() => {
                  closeBuildingReview();
                  closeRightPanel();
                }}
                onSaved={() => {
                  triggerBuildingReload();
                }}
              />
            ) : selectedCensusRadio ? (
              /* Panel de detalle de radio censal 2022 */
              <CensusRadioDetailsPanel
                radio={selectedCensusRadio}
                onClose={() => {
                  clearCensusRadio();
                  closeRightPanel();
                }}
              />
            ) : selectedUrbanEntity ? (
              /* Panel de detalle de entidad urbana — Fase 2A */
              <UrbanEntityDetailsPanel
                entity={selectedUrbanEntity}
                projectId={projectId ?? ''}
                onClose={() => {
                  selectUrbanEntity(null);
                  closeRightPanel();
                }}
              />
            ) : null}
          </RightPanel>
        </>
      )}

      {/* Banner de modo Planificación (flotante sobre el mapa) */}
      {activeScenario && <ScenarioBanner scenario={activeScenario} />}

      {/* Banner de dibujo de ruta (flotante sobre el mapa) */}
      {showRouteDrawingBanner && (
        <div
          style={{
            position: 'fixed',
            top: '200px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: 'linear-gradient(135deg, rgba(47, 164, 181, 0.6) 0%, rgba(30, 122, 138, 0.6) 100%)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontFamily: 'var(--font-family)',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideDown 0.3s ease-out',
          }}
        >
          <RouteIcon size={20} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', marginBottom: '4px' }}>Dibujando ruta de recolección</strong>
            <span style={{ opacity: 0.9, fontSize: '13px' }}>
              Haga clic para agregar puntos. Presione <kbd style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
              }}>Enter</kbd> para finalizar o <kbd style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
              }}>Esc</kbd> para cancelar.
            </span>
          </div>
          <button
            onClick={cancelRouteDrawing}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            title="Cancelar dibujo y cerrar panel"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Área de contenido fullscreen (mapa) */}
      <main
        className={`map-content ${
          activeTool === 'create-tree' || activeTool === 'add-container' || activeTool === 'draw-route'
            ? 'tool-active-create'
            : activeTool === 'measurement'
            ? 'tool-active-measurement'
            : ''
        } ${isEditingActive && hasUnsavedChanges ? 'editing-locked' : ''}`}
        onClick={handleMapClick}
        style={{
          cursor: isEditingActive && hasUnsavedChanges ? 'not-allowed' : undefined,
        }}
      >
        {/* Fase 2C: Renderizar MapViewer con bounds reales o cacheados.
            - Si hay bounds reales (del API): usar esos.
            - Si solo hay cache: usar cache para crear mapa de inmediato.
            - initialBounds solo se usa en el mount inicial; cambios posteriores
              se reconcilian via fitBounds en el useEffect de reconciliación. */}
        {(currentProject?.bounds ?? cachedBootstrap?.bounds) && (
          <MapViewer
            style={mapStyleService.getDefaultStyle()}
            height="100%"
            width="100%"
            showControls={true}
            showScale={true}
            initialBounds={(currentProject?.bounds ?? cachedBootstrap!.bounds) as {
              minLng: number; minLat: number; maxLng: number; maxLat: number;
            }}
            onMapShellReady={handleMapShellReady}
            onMapStyleReady={handleMapStyleReady}
            onMapLoad={handleMapLoad}
          />
        )}
        {/* Los markers de árboles se renderizan automáticamente como Markers de MapLibre GL */}
      </main>

      {/* Controles de Undo/Redo (flotantes, solo en modo edición) */}
      {showPanels && <UndoRedoControls />}

      {/* Leyenda de Space Syntax (flotante, solo cuando syntaxModeActive) */}
      {/* Fase 1: reubicada a bottom-right para evitar superposición con LeftPanel */}
      {showPanels && <SyntaxLegend position="bottom-right" />}

      {/* Leyenda de Población por segmento: integrada en LeftPanel como PopulationAnalysisPanel */}
      {/* PopulationSegmentLegend flotante eliminada en V1.9.2 — ya no necesaria */}

      {/* Leyenda de indicadores (flotante, solo en modo analizar con visualización activa) */}
      {showPanels && activeIndicatorVisualization && operationalMode === 'analyze' && (
        <IndicatorLegend
          indicatorType={activeIndicatorVisualization.indicatorType}
          classification={activeIndicatorVisualization.classification}
          onClose={() => setActiveIndicatorVisualization(null)}
        />
      )}

      {/* Preview de elemento antes de crear */}
      {elementPreview && (
        <ElementPreview
          type={elementPreview.type}
          coordinates={elementPreview.coordinates}
          onConfirm={handleConfirmCreate}
          onCancel={handleCancelCreate}
        />
      )}

      {/* Menú contextual de edge */}
      {edgeContextMenu && (() => {
        // Determinar si el edge ya es recto (solo 2 puntos)
        const edgeFeature = canonicalNetworkStore.getEdge(edgeContextMenu.edgeId);
        const coordsCount = edgeFeature?.geometry?.coordinates?.length ?? 0;
        const isAlreadyStraight = coordsCount <= 2;

        return (
          <EdgeContextMenu
            position={edgeContextMenu.position}
            edgeId={edgeContextMenu.edgeId}
            isAlreadyStraight={isAlreadyStraight}
            onEditShape={() => {
              closeEdgeContextMenu();
              startGeometryEdit(edgeContextMenu.edgeId);
            }}
            onSimplify={() => {
              closeEdgeContextMenu();
              simplifyEdgeToLine(edgeContextMenu.edgeId);
            }}
            onInsertNode={() => {
              closeEdgeContextMenu();
              startInsertNodeOnEdge(edgeContextMenu.edgeId);
            }}
            onAddToSelection={() => {
              closeEdgeContextMenu();
              void addEdgeToSelection(edgeContextMenu.edgeId);
            }}
            onDelete={() => {
              closeEdgeContextMenu();
              promptDeleteEdge(edgeContextMenu.edgeId, edgeContextMenu.position);
            }}
            onClose={closeEdgeContextMenu}
          />
        );
      })()}

      {/* Menú contextual de nodo */}
      {nodeContextMenu && (
        <NodeContextMenu
          position={nodeContextMenu.position}
          nodeId={nodeContextMenu.nodeId}
          onExtendNetwork={() => {
            closeNodeContextMenu();
            startBranchCreation(nodeContextMenu.nodeId);
          }}
          onDelete={() => {
            closeNodeContextMenu();
            promptDeleteNode(nodeContextMenu.nodeId, nodeContextMenu.position);
          }}
          onClose={closeNodeContextMenu}
        />
      )}

      {/* Confirmación de edición de red canónica */}
      {confirmation && (() => {
        console.log('[MapLayout] 🎨 Rendering NetworkEditConfirmation:', {
          mode: confirmation.mode,
          position: confirmation.position,
          hasMetadata: !!confirmation.metadata,
        });
        return true;
      })() && (
        <NetworkEditConfirmation
          mode={confirmation.mode}
          coordinates={confirmation.position as [number, number]}
          metadata={confirmation.metadata}
          onConfirm={async () => {
            if (confirmation.mode === 'split-segment') {
              await confirmSegmentSplit();
            } else if (confirmation.mode === 'delete-node') {
              await confirmDeleteNode();
            } else if (confirmation.mode === 'delete-edge') {
              await confirmDeleteEdge();
            }
          }}
          onCancel={cancelEdit}
        />
      )}

      {/* Indicador de modo de edición activo */}
      {editMode && (
        <NetworkEditModeIndicator
          mode={editMode}
          hasPreview={!!previewNode}
          onCancel={cancelEdit}
        />
      )}

      {/* Indicador de estado de sincronización */}
      <div style={{ position: 'absolute', top: 80, right: 16, zIndex: 1000 }}>
        <SyncStatusIndicator />
      </div>

      {/* Popover de celda en el mapa */}
      {showPanels && cellPopoverPosition && selectedCellId && (
        <CellMapPopover
          position={cellPopoverPosition}
          onClick={() => {
            if (selectedCellId) {
              handleCellStatsClick(selectedCellId);
            }
          }}
          onClose={() => {
            setCellPopoverPosition(null);
          }}
        />
      )}

      {/* Toolbar inferior - PERMANENTE (core) */}
      {showPanels && (
        <BottomToolbar
          activeTool={activeTool}
          onToolChange={handleToolChange}
        />
      )}

      {/* Undo/Redo keyboard handler */}
      <UndoRedoKeyboardHandler />

      {/* Network Undo/Redo keyboard handler */}
      <NetworkUndoRedoHandler />

      {/* Mid-point handle para dividir edges */}
      {midPoint && (
        <MidPointHandle
          screenCoordinates={midPoint.screenCoordinates}
          isNear={isNearMidPoint}
          onClick={() => {
            console.log('[MapLayout] Mid-point clicked, starting split');
            startSegmentSplit(midPoint.edgeId, midPoint.geoCoordinates);
          }}
        />
      )}

      {/* Indicador visual de snap REMOVIDO - el edge cyan + preview cyan son suficientes
          y este indicador usaba el snapTarget del estado (desfasado 1-2 ciclos de render)
          causando que apareciera en posición incorrecta ("ghost node") */}

      {/* Debug component (presiona 'D' para toggle) */}
      {/* <ModuleStoreDebug /> */}

      {/* Wizard de importación edilicia por sesión — E3-D */}
      <BuildingImportWizardModal />
    </div>
  );
}

/**
 * MapLayout - Wrapper con providers
 *
 * @remarks
 * Envuelve MapLayoutContent con los providers necesarios:
 * - GridsProvider: Gestión de mallas H3 (transversal a todos los módulos)
 * - MapToolsProvider: Herramientas del mapa
 * - RightPanelProvider: Gestión del panel derecho y cambios sin guardar
 *
 * IMPORTANTE: MapLayoutContent NUNCA se desmonta al cambiar módulo.
 * Esto garantiza que:
 * - El mapa mantiene su posición y zoom
 * - Las mallas visibles persisten entre módulos
 * - El estado del mapa es transversal a todos los módulos
 *
 * WasteProvider se inyecta SOLO alrededor de WasteTabbedPanel cuando
 * el módulo waste está activo, evitando remontaje del mapa.
 */
export function MapLayout({ showPanels = true, visibleModes }: MapLayoutProps): JSX.Element {
  return (
    <EditLockProvider>
      <UrbanNetworkProvider>
        <NetworkStyleProvider>
          <SyntaxAnalysisProvider>
            <PopulationSegmentProvider>
              <NetworkEditProvider>
                <BaseNetworkProvider>
                  <GridsProvider>
                    <MapToolsProvider>
                      <RightPanelProvider>
                        <MapLayoutContent showPanels={showPanels} visibleModes={visibleModes} />
                      </RightPanelProvider>
                    </MapToolsProvider>
                  </GridsProvider>
                </BaseNetworkProvider>
              </NetworkEditProvider>
            </PopulationSegmentProvider>
          </SyntaxAnalysisProvider>
        </NetworkStyleProvider>
      </UrbanNetworkProvider>
    </EditLockProvider>
  );
}
